'use client';

import { SettingsTabProps } from '@/types/settings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useId,
} from 'react';
import { SettingsHeader } from '../components/SettingsHeader';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import { useSession } from 'next-auth/react';
import { Session, User } from 'next-auth';
import { toast } from '@/utils/toastManager';
import { useToastManager } from '@/hooks/useToastManager';
import { useDebounce } from '@/hooks/useDebounce';
import posthog from '@/lib/posthog';
import {
  useRepositoriesPage,
  unifiedCodebaseQueryKeys,
} from '@/hooks/useUnifiedCodebaseManagement';
import {
  useCodebaseReposPage,
  useSyncCodebaseRepos,
  codebaseReposQueryKeys,
} from '@/hooks/useCodebaseRepos';
import SyncReposButton from '@/components/SyncReposButton';
import { useSearchParams, useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import PermissionInfoModal from '@/components/ModalAddRepository/PermissionInfoModal';
import InstallPrBotModal from '@/components/Settings/modals/InstallPrBotModal';
import { DisableReposMultiSelectModal } from '../modals/DisableReposMultiSelectModal';
import {
  togglePRBotEnabled,
  updatePRBotSettingsForOrg,
} from '@/apis/togglePRBotEnabled';
import { setAgentInsightsRepoCollection } from '@/apis/agentInsightsRepoCollection';
import { toggleDedup } from '@/apis/toggleDedup';
import { toggleAutoApprove } from '@/apis/toggleAutoApprove';
import {
  configurePRBotForOrgGitlab,
  configurePRBotForOrgBitbucket,
  configurePRBotForOrgBitbucketBulk,
} from '@/apis/prReviewsConfig';
import BitbucketAppPasswordModal from '@/components/BitbucketAppPasswordModal';
import { useBitbucketOrgToken } from '@/hooks/useBitbucketOrgToken';
import { useGitHubPermissions } from '@/hooks/useGitHubPermissions';
import { Button as ShadcnButton } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Plus,
  X,
} from 'lucide-react';
import { Info } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useInstallationSyncStatus } from '@/hooks/useInstallationSyncStatus';
import { InstallationSyncBanner } from '@/components/InstallationSyncBanner';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isAdminOrOwner } from '@/lib/rbac';
import AttributionSettingsCard from './AttributionSettingsCard';
import { useBitbucketProjects } from '@/hooks/useBitbucketProjects';
import { useGitLabGroups } from '@/hooks/useGitLabGroups';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { githubInstallUrl } from '@/lib/utils/githubInstallUrl';
import { fetchOrgCombiningInfo } from '@/apis/crossOrgCombining';
import { searchGithubOrgs } from '@/apis/searchProviderOrganization';
import { isRepoInOrgScope, repoPathFromUrl } from '@/lib/utils/gitOrgScope';
import { isMissingInstallationError } from '@/lib/utils/prBotInstallation';

type ToggleField =
  | 'enable_auto_comments'
  | 'enable_documentation'
  | 'enable_security'
  | 'pr_evaluation';

const TOGGLE_LABELS: Record<ToggleField, string> = {
  enable_auto_comments: 'PR Review',
  enable_documentation: 'Documentation',
  enable_security: 'Security',
  pr_evaluation: 'PR Evaluation',
};

interface Repository {
  name: string;
  description?: string;
  status: string;
  prReviewBot: boolean;
  enableAutoComments: boolean;
  enableDocumentation: boolean;
  enableSecurity: boolean;
  enableDedup: boolean;
  autoApproveOnSafe: boolean;
  agentInsightsEnabled: boolean;
  /** PR bot installed on this repo. False = row shows Add Repo instead of the toggles. */
  hasPRBot: boolean;
  issueBot: boolean;
  lastSync: string;
  dependencies: string[];
  isBidirectional: boolean;
  repoUUID?: string;
  url: string;
}

function RepositoriesTableSkeleton() {
  const placeholderRows = Array.from({ length: 8 });

  return (
    <div className="bg-ds-surface-white rounded-lg border border-ds-border-subtlest overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-ds-text-subtle/20 scrollbar-track-ds-gray-subtlest">
        <table
          className="w-full min-w-[1012px]"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '44px' }} />
            <col style={{ width: '280px' }} />
            <col style={{ width: '128px' }} />
            <col style={{ width: '96px' }} />
            <col style={{ width: '96px' }} />
            <col style={{ width: '96px' }} />
            <col style={{ width: '96px' }} />
            <col style={{ width: '112px' }} />
          </colgroup>
          <thead className="bg-ds-gray-subtlest border-b border-ds-border-subtlest">
            <tr>
              <th
                className="px-3 py-2 text-center align-middle"
                style={{ width: '44px' }}
                aria-label="Select row"
              />
              <th className="pl-0 pr-4 py-2 text-left text-xs font-medium text-ds-text-subtle tracking-wider">
                Repo
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                Linked Repos
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default inline-flex items-center gap-1">
                          PR Review
                          <Info size={14} className="text-ds-text-subtle/70" />
                        </span>
                      }
                    />
                    <TooltipContent
                      side="top"
                      className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                    >
                      Enable or disable automated code review comments on pull
                      requests for this repository. For metrics and scoring, see
                      PR Evaluation.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default inline-flex items-center gap-1">
                          PR Evaluation
                          <Info size={14} className="text-ds-text-subtle/70" />
                        </span>
                      }
                    />
                    <TooltipContent
                      side="top"
                      className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                    >
                      Enable or disable PR evaluation for this repository.
                      Required for Team Insights such as reviews, metrics, and
                      scoring.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default inline-flex items-center gap-1">
                          Deduplication
                          <Info size={14} className="text-ds-text-subtle/70" />
                        </span>
                      }
                    />
                    <TooltipContent
                      side="top"
                      className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                    >
                      Enable or disable deduplication of PR reviews. When
                      enabled, duplicate review comments are suppressed.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default inline-flex items-center gap-1">
                          Auto-approve at 5/5
                          <Info size={14} className="text-ds-text-subtle/70" />
                        </span>
                      }
                    />
                    <TooltipContent
                      side="top"
                      className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                    >
                      Submit a GitHub approval (not merge) when the review
                      scores 5/5 (Safe to Merge). Overrides the org-level
                      default for this repository.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider">
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="cursor-default inline-flex items-center gap-1">
                          Agent Insights
                          <Info size={14} className="text-ds-text-subtle/70" />
                        </span>
                      }
                    />
                    <TooltipContent
                      side="top"
                      className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                    >
                      Collect agent-session insights (spend, tasks, behaviour)
                      from coding agents run against this repository. Turning it
                      off stops future collection; insights already collected
                      are kept.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
            </tr>
          </thead>
          <tbody className="bg-ds-surface-white">
            {placeholderRows.map((_, n) => (
              <tr
                key={`skeleton-row-${n}`}
                className="border-b border-ds-border-subtlest"
              >
                <td
                  className="px-3 py-4 text-center align-middle"
                  style={{ width: '44px' }}
                >
                  <div className="h-4 w-4 mx-auto bg-ds-gray-subtlest rounded animate-pulse" />
                </td>
                <td className="pl-0 pr-4 py-4">
                  <div className="h-3 w-40 bg-ds-gray-subtlest rounded mb-2 animate-pulse" />
                  <div className="h-2 w-64 bg-ds-gray-subtlest rounded animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-3 w-20 mx-auto bg-ds-gray-subtlest rounded animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-5 w-10 mx-auto bg-ds-gray-subtlest rounded-full animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-5 w-10 mx-auto bg-ds-gray-subtlest rounded-full animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-5 w-10 mx-auto bg-ds-gray-subtlest rounded-full animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-5 w-10 mx-auto bg-ds-gray-subtlest rounded-full animate-pulse" />
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="h-5 w-10 mx-auto bg-ds-gray-subtlest rounded-full animate-pulse" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PROVIDER_FILTER_ALL = '__all__';

/** In-flight bulk PR-review enables. Bounded because each one is a provider API
 *  call (GitLab installs a webhook), and providers rate-limit per token. */
const BULK_ENABLE_CONCURRENCY = 6;

interface BitbucketProjectOption {
  key: string;
  name: string;
}

function BitbucketProjectCombobox({
  projects,
  isLoading,
  selectedKey,
  onSelect,
}: {
  projects: BitbucketProjectOption[];
  isLoading: boolean;
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const selected = projects.find((p) => p.key === selectedKey);
  const label = isLoading
    ? 'Loading projects…'
    : selected
      ? selected.name
      : 'Filter by Bitbucket project';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            disabled={isLoading}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-ds-border-subtler bg-ds-surface-white px-3 py-2 text-sm text-ds-text-default',
              'focus:outline-none focus:ring-2 focus:ring-ds-brand-subtler',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            <span
              className={cn('truncate', !selected && 'text-ds-text-subtle')}
            >
              {label}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        }
      />
      <PopoverContent id={listboxId} className="w-56 p-0" align="start">
        <Command
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Search projects…" />
          <CommandList>
            <CommandEmpty>No projects found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all projects"
                onSelect={() => {
                  onSelect('');
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    selectedKey === '' ? 'opacity-100' : 'opacity-0'
                  )}
                />
                All projects
              </CommandItem>
              {projects.map((p) => (
                <CommandItem
                  key={p.key}
                  value={`${p.name} ${p.key}`}
                  onSelect={() => {
                    onSelect(p.key);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedKey === p.key ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{p.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function CodeManagement({ className }: SettingsTabProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedBitbucketProjectKey, setSelectedBitbucketProjectKey] =
    useState<string>('');
  // Owner filter for GitHub. A workspace on several orgs can hold thousands of
  // repos, and the listing pages 20 at a time, so this has to reach the query
  // rather than filter what is already rendered.
  const [selectedGitOrg, setSelectedGitOrg] = useState('');
  const [selectedGitlabGroupPath, setSelectedGitlabGroupPath] =
    useState<string>('');
  const [togglingRepos, setTogglingRepos] = useState<Set<string>>(new Set());
  const [showPermissionInfo, setShowPermissionInfo] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);

  // Unified provider modal state (GitLab / Bitbucket)
  const [providerModal, setProviderModal] = useState<{
    provider: 'gitlab' | 'bitbucket';
    repoUrl: string;
    groupAccessToken?: string;
    isLoading: boolean;
  } | null>(null);

  // GitHub App install dialog. Open before leaving for github.com so the page
  // does not simply vanish on click: repo selection happens on GitHub's screen,
  // which is not obvious from a button labelled "Add Repo". `repoName` is set
  // when the dialog is opened from a row, so the copy can name that repo.
  const [installModal, setInstallModal] = useState<{
    repoName?: string;
    /**
     * Provider account id to preselect on GitHub's install page. Set when the
     * dialog is opened right after linking an org, so the install targets THAT
     * org rather than whichever one is first in the workspace scope.
     */
    /**
     * The org to install into, when the dialog was opened right after linking
     * one. Id and login are a single value on purpose: three separate bugs in
     * this flow came from letting them drift apart, so a shape where one can be
     * present without the other simply does not exist here.
     *
     * Absent means "the workspace default", which is what the header Add Repo
     * button and a row's Grant access want.
     */
    target?: { id: string; login: string };
  } | null>(null);

  // Stores the repo URL that was being configured when the token modal opened,
  // so we can resume the flow after the user saves the App Password.
  const pendingBitbucketRepoUrlRef = useRef<string | null>(null);

  // Confirmation modal for toggle changes (PR Review, Documentation, Security, PR Evaluation)
  const [confirmToggle, setConfirmToggle] = useState<{
    repo: Repository;
    field: ToggleField;
    currentValue: boolean;
  } | null>(null);

  // Multi-select modal for disabling PR Review / PR Evaluation across multiple repos
  const [multiSelectModal, setMultiSelectModal] = useState<{
    field: 'enable_auto_comments' | 'pr_evaluation';
    repo: Repository;
    /** When true, only show repos that are in selectedRepos (e.g. when opened from bottom bar) */
    restrictToSelected?: boolean;
  } | null>(null);

  // Row selection for bulk actions (Figma node 1-2106: checkboxes on table rows)
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());

  // "Apply to every repo matching the current filter", not just the page.
  //
  // The header checkbox can only ever select the 20 rows the listing returned,
  // so configuring a large org meant paging through it (243 repos here, 1,803
  // on the biggest customer org). When this is on, the bulk actions stop
  // sending a list of names and send the FILTER instead, and the server applies
  // the change to everything that matches in one statement.
  const [applyToAllMatching, setApplyToAllMatching] = useState(false);

  /**
   * Drop the whole selection: the rows AND the all-matching mode.
   *
   * The two are separate pieces of state, so anything that clears only
   * `selectedRepos` leaves the mode armed. Selecting one row afterwards would
   * then send a filter-wide update from what looks like a single-row
   * selection. One function so a new clear site cannot forget the other half.
   */
  const clearSelection = useCallback(() => {
    setSelectedRepos(new Set());
    setApplyToAllMatching(false);
  }, []);

  // Any change to what is being looked at invalidates "all matching" — the set
  // it referred to no longer exists. Leaving it on would apply a change to a
  // scope the user can no longer see.
  useEffect(() => {
    // Both, not just the mode. The names in `selectedRepos` belong to the
    // previous filter, so leaving them keeps the bulk bar open over a scope the
    // user is no longer looking at.
    setApplyToAllMatching(false);
    setSelectedRepos(new Set());
  }, [
    debouncedSearchTerm,
    selectedGitOrg,
    selectedGitlabGroupPath,
    selectedBitbucketProjectKey,
  ]);

  // Progress tracking for the bulk PR Review enable flow so the bottom bar can
  // render a spinner and counter while configurePRBotForOrgGitlab runs per repo.
  const [bulkEnableProgress, setBulkEnableProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  const { user } = useUserStore();
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  // Numbered pagination kept in local state (resets to page 1 on reload).
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  // Bumped after a PR bot settings write has refetched. Keys the row container
  // so it remounts and replays the `rows-resort` sweep over the new order. The
  // listing sorts PR-Evaluation-ON repos first, so a write reshuffles the page
  // and the incoming row would otherwise look like a toggle that bounced back.
  const [resortToken, setResortToken] = useState(0);

  // Provider detection (used for the project/group filter dropdown)
  const {
    user: loggedUser,
    organization: loggedOrg,
    orgResolutionDone,
  } = useUserLogged();
  const providerRaw = loggedOrg?.Source ?? loggedUser?.provider ?? 'github';
  const provider =
    typeof providerRaw === 'string' ? providerRaw.toLowerCase() : 'github';
  const isBitbucketProvider = provider === 'bitbucket';
  const isGitlabProvider = provider === 'gitlab';

  // The orgs currently combined into this dashboard, each with the provider
  // account id GitHub's install page preselects. Sourced from the combining
  // info rather than loggedOrg.GitOrgName, which only ever described the HOME
  // org's own scope: with a different org linked and home unlinked, Add Repo
  // offered the home org and sent people to an install page for repos they
  // are not even looking at.
  const [installOrgTargets, setInstallOrgTargets] = useState<
    { login: string; gitOrgId?: string; isHome?: boolean }[]
  >([]);

  // Git orgs this workspace is scoped to. Drives the table's "Filter by
  // organization" control.
  //
  // Sourced from the COMBINED set, not loggedOrg.GitOrgName alone. That field
  // is Path A's comma-separated list — one OrganizationModel holding several
  // GitHub orgs. Under Path B, which is how orgs are combined now, each GitHub
  // org is its own OrganizationModel and so GitOrgName holds exactly one login.
  // Reading only that field left `length === 1` for every cross-org viewer, and
  // the filter is gated on `> 1` — so the control silently never rendered for
  // precisely the people who need it, while the table happily showed repos from
  // all their orgs mixed together.
  //
  // Same bug the Add Repo picker already fixed below (installOrgTargets); this
  // is the half that was missed, so the two now agree about what is connected.
  const linkedGitOrgs = useMemo(() => {
    const seen = new Map<string, string>();
    const add = (login: string | undefined) => {
      const name = (login ?? '').trim();
      if (!name) return;
      // De-dupe case-insensitively but keep the first real casing: "freshdesk"
      // and "Freshdesk" are one org, and the filter value must match what the
      // listing matches on.
      const key = name.toLowerCase();
      if (!seen.has(key)) seen.set(key, name);
    };
    (loggedOrg?.GitOrgName ?? '').split(',').forEach(add);
    installOrgTargets.forEach((t) => add(t.login));
    return Array.from(seen.values());
  }, [loggedOrg?.GitOrgName, installOrgTargets]);

  const homeOrgUuid = organization?.OrgUUID ?? '';
  // Every token, not just the first: a legacy Path A workspace is scoped to
  // several GitHub accounts, and offering only the first hides the others'
  // repositories from Add Repo entirely.
  const homeGitOrgNames = (loggedOrg?.GitOrgName ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .join(',');
  const homeGitOrgId = loggedOrg?.GitOrgId;

  useEffect(() => {
    let cancelled = false;
    fetchOrgCombiningInfo()
      .then((info) => {
        if (cancelled || info.Error) return;
        const targets: {
          login: string;
          gitOrgId?: string;
          isHome?: boolean;
        }[] = [];

        /**
         * One entry per GitHub account an org is scoped to.
         *
         * Shown with its real casing, not the lowercased matching copy --
         * "nxt-2026" beside "Entelligence-AI" reads as a different kind of
         * thing rather than the same kind from another org.
         *
         * The stored account id belongs to the FIRST token only, so the rest
         * carry none and resolve through eligibleOrgIds at click time. Giving
         * them the first token's id would send every one of them to the same
         * wrong account.
         */
        const addTargets = (
          displayNames: string | null | undefined,
          fallbackLogins: string[] | undefined,
          firstTokenId: string | undefined,
          isHome = false
        ) => {
          const logins = (displayNames ?? '')
            .split(',')
            .map((token) => token.trim())
            .filter(Boolean);
          const resolved = logins.length ? logins : (fallbackLogins ?? []);
          resolved.forEach((login, index) => {
            targets.push({
              login,
              gitOrgId: index === 0 ? firstTokenId || undefined : undefined,
              isHome,
            });
          });
        };

        // Home carries no membership row of its own -- it is linked by
        // default and only ever appears in LinkedOrgUUIDs, so its logins and
        // id come from the session's org.
        if (info.LinkedOrgUUIDs.includes(homeOrgUuid)) {
          addTargets(
            homeGitOrgNames,
            undefined,
            homeGitOrgId ?? undefined,
            true
          );
        }
        for (const membership of info.OtherOrgMemberships) {
          if (!info.LinkedOrgUUIDs.includes(membership.OrgUUID)) continue;
          addTargets(
            membership.GitOrgName,
            membership.GitOrgLogins,
            membership.GitOrgId ?? undefined
          );
        }
        setInstallOrgTargets(targets);
      })
      .catch(() => {
        /* falls back to the workspace default target */
      });
    return () => {
      cancelled = true;
    };
  }, [homeOrgUuid, homeGitOrgNames, homeGitOrgId]);

  /** Logins offered by the install dialog: one linked org means no choice to
   * make, so the dialog auto-targets it and shows which one it is. */
  const installOrgChoices = useMemo(
    () => installOrgTargets.map((t) => t.login),
    [installOrgTargets]
  );

  /**
   * Every git org this view can legitimately connect a repo from, as the
   * comma-separated shape `isRepoInOrgScope` expects.
   *
   * The scope guards compared a repo's owner against the HOME org's
   * git_org_name alone. The table now lists repositories from every linked
   * org, so with home unlinked, clicking Add Repo on a linked org's own
   * repository was refused as "outside your connected org" -- a valid install
   * path blocked by a guard that had not been told what the workspace now
   * connects. Falls back to the home org's scope until the combining info
   * lands, which is the previous behaviour.
   */
  const connectedGitOrgNames = useMemo(
    () =>
      installOrgTargets.length
        ? installOrgTargets.map((t) => t.login).join(',')
        : (loggedOrg?.GitOrgName ?? ''),
    [installOrgTargets, loggedOrg?.GitOrgName]
  );

  /**
   * Install redirect with a git org preselected when known.
   *
   * Prefers the sole linked org over the session's own: with exactly one org
   * linked there is nothing to choose, and sending the caller to GitHub's
   * account picker there is the bug this exists to fix.
   */
  const buildInstallUrl = useCallback(
    (returnTo: string) => {
      // Nothing resolved yet (the combining info is still loading): the
      // session's own org is the only thing known, and is today's behaviour.
      if (!installOrgTargets.length) {
        return githubInstallUrl(returnTo, loggedOrg?.GitOrgId);
      }
      // Exactly one target is unambiguous. If its account id is unknown, fall
      // through to GitHub's own picker -- substituting the home account would
      // open the install page for a DIFFERENT org, which reads as success and
      // is the failure this whole flow exists to prevent.
      if (installOrgTargets.length === 1) {
        return githubInstallUrl(returnTo, installOrgTargets[0].gitOrgId);
      }
      // Several targets and no explicit pick. The home account is the only
      // defensible default, and only when home is actually linked -- a single
      // linked workspace scoped to several GitHub accounts also lands here,
      // and defaulting it to home would target an org the caller unlinked.
      const homeTarget = installOrgTargets.find((t) => t.isHome);
      return githubInstallUrl(returnTo, homeTarget?.gitOrgId);
    },
    [installOrgTargets, loggedOrg?.GitOrgId]
  );

  const { projects: bitbucketProjects, isLoading: isLoadingBitbucketProjects } =
    useBitbucketProjects();
  const { groups: gitlabGroups, isLoading: isLoadingGitlabGroups } =
    useGitLabGroups();

  // Only forward filter values for the matching provider; ignore otherwise.
  const bbProjectKeysForRequest = useMemo(
    () =>
      isBitbucketProvider && selectedBitbucketProjectKey
        ? [selectedBitbucketProjectKey]
        : undefined,
    [isBitbucketProvider, selectedBitbucketProjectKey]
  );
  const gitlabGroupPathForRequest =
    isGitlabProvider && selectedGitlabGroupPath
      ? selectedGitlabGroupPath
      : undefined;

  // Transition scaffolding (removed in the Bitbucket phase): Bitbucket keeps
  // the old provider-backed endpoint (its project filter needs the live
  // listing); GitHub/GitLab read the DB-backed listing (instant, complete,
  // independent of the requester's OAuth token). Exactly one hook is enabled.
  const bitbucketPage = useRepositoriesPage(
    organization?.OrgUUID || '',
    user?.UserUUID || '',
    {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearchTerm.trim(),
      enabled: Boolean(organization?.OrgUUID) && isBitbucketProvider,
      bitbucketProjectKeys: bbProjectKeysForRequest,
    }
  );
  const dbPage = useCodebaseReposPage(
    organization?.OrgUUID || '',
    user?.UserUUID || '',
    {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearchTerm.trim(),
      // PR Evaluation ON first, then name — matches list endpoint enabled_first.
      // Dropped while searching: the list endpoint only applies its search
      // relevance ranking when sortBy is the default 'name', and an explicit
      // sort wins over it. Sending enabled_first through a search meant a
      // matching repo could rank past the 20-row page and look like no match.
      sortBy: debouncedSearchTerm.trim() ? undefined : 'enabled_first',
      enabled: Boolean(organization?.OrgUUID) && !isBitbucketProvider,
      gitlabGroupPath: gitlabGroupPathForRequest,
      // Only meaningful for GitHub; GitLab narrows by group above.
      gitOrg: isGitlabProvider ? undefined : selectedGitOrg || undefined,
    }
  );
  const {
    repositories: unifiedRepositories,
    isLoading: isLoadingRepositories,
    isFetching: isFetchingRepositories,
    error: repositoriesError,
    refetch: refetchRepositories,
    total_count: reposTotalCount,
    has_more: reposHasMore,
    apiError: reposApiError,
    agent_insights_org_default: agentInsightsOrgDefault,
  } = isBitbucketProvider ? bitbucketPage : dbPage;

  useEffect(() => {
    if (reposApiError) {
      console.error('Failed to load repositories:', reposApiError);
    }
  }, [reposApiError]);

  /**
   * Refetch after a PR bot settings write, then replay the row sweep.
   * Awaiting first matters: bumping the token before the new page lands would
   * animate the stale order and leave the reshuffle itself unanimated.
   */
  const refetchAndSweepRows = useCallback(async () => {
    await refetchRepositories();
    setResortToken((t) => t + 1);
  }, [refetchRepositories]);

  // Reset to first page when a filter selection changes.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [selectedBitbucketProjectKey, selectedGitlabGroupPath, selectedGitOrg]);

  // Reset to first page whenever the search term changes.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearchTerm]);

  const {
    status: installationSyncStatus,
    progressData: installationSyncProgressData,
    refetch: refetchSyncStatus,
  } = useInstallationSyncStatus(organization?.OrgUUID);

  useEffect(() => {
    if (installationSyncStatus === 'COMPLETED') {
      refetchRepositories();
      refetchSyncStatus();
    }
  }, [installationSyncStatus, refetchRepositories, refetchSyncStatus]);

  const handleRetryInstallation = () => {
    window.location.href = buildInstallUrl(
      '/settings?tab=code-management&repo_sync=1'
    );
  };

  // Auto-sync on return from the provider app-install flow (?repo_sync=1 in
  // return_to). The install webhook writes new repos to the DB asynchronously;
  // an explicit sync makes the newly granted repos show up without a manual
  // refresh. Fires once, then strips the marker from the URL.
  const { sync: syncRepos } = useSyncCodebaseRepos();
  const searchParams = useSearchParams();
  const router = useRouter();
  const autoSyncFiredRef = useRef(false);
  useEffect(() => {
    if (autoSyncFiredRef.current) return;
    if (searchParams.get('repo_sync') !== '1') return;
    if (!organization?.OrgUUID) return;
    autoSyncFiredRef.current = true;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('repo_sync');
    router.replace(`/settings?${params.toString()}`, { scroll: false });
    void syncRepos(organization.OrgUUID, user?.UserUUID || '');
  }, [searchParams, organization?.OrgUUID, user?.UserUUID, router, syncRepos]);

  // Open the install dialog on arrival from a successful "Add organization"
  // (?install_prompt=1). Linking an org writes the workspace scope but grants
  // the PR bot nothing, so this is the step that actually connects its repos.
  // Fires once, then strips the marker from the URL.
  //
  // Gated on the workspace's provider, not the caller's: the dialog leads to
  // the GitHub App install page, and a GitLab or Bitbucket workspace grants
  // repo access through its own credential modal instead. The marker is a URL
  // parameter, so this has to hold even when nothing legitimate set it.
  const installPromptFiredRef = useRef(false);
  useEffect(() => {
    if (installPromptFiredRef.current) return;
    if (searchParams.get('install_prompt') !== '1') return;
    // Wait for org resolution to genuinely finish before consuming the marker.
    // `provider` falls back to 'github' while loggedOrg is still loading, so
    // acting early would open a GitHub dialog on a GitLab workspace AND strip
    // the marker, leaving the corrected render with nothing to act on.
    //
    // orgResolutionDone rather than a truthiness test on loggedOrg.Source: the
    // workspace is the authoritative provider here (a member can be signed in
    // with a different identity than the org's git provider), and only this
    // flag separates "no workspace" from "not loaded yet". Testing Source
    // directly would also strand a workspace whose Source is legitimately
    // empty, where the session fallback is the intended answer.
    if (!orgResolutionDone) return;
    installPromptFiredRef.current = true;
    const targetId = searchParams.get('target_id');
    const targetLogin = searchParams.get('target_org');
    const params = new URLSearchParams(searchParams.toString());
    params.delete('install_prompt');
    params.delete('target_id');
    params.delete('target_org');
    router.replace(`/settings?${params.toString()}`, { scroll: false });
    if (isGitlabProvider || isBitbucketProvider) return;
    // Both or neither. A URL carrying only one of them falls back to the
    // workspace default rather than mixing a target with a name that does not
    // describe it.
    setInstallModal({
      target:
        targetId && targetLogin
          ? { id: targetId, login: targetLogin }
          : undefined,
    });
  }, [
    searchParams,
    router,
    isGitlabProvider,
    isBitbucketProvider,
    orgResolutionDone,
  ]);

  const isSyncInProgress = installationSyncStatus === 'IN_PROGRESS';
  const isSyncFailed = installationSyncStatus === 'FAILED';

  const { data: session } = useSession() as {
    data:
      | (Session & { user: User & { accessToken: string; provider: string } })
      | null;
  };

  // login -> provider account id, for the orgs this admin can see. GitHub's
  // install page keys off the account id (`suggested_target_id`), but the
  // dialog works in names, so the mapping lives here. Empty until it loads;
  // an unresolved name just falls back to the workspace default.
  const [eligibleOrgIds, setEligibleOrgIds] = useState<Record<string, number>>(
    {}
  );
  const sessionAccessToken = (
    session?.user as { accessToken?: string } | undefined
  )?.accessToken;
  useEffect(() => {
    if (isGitlabProvider || isBitbucketProvider) return;
    // The workspace being GitHub is not enough: this calls GitHub's API with
    // the SESSION's token, so an admin signed in through GitLab would send a
    // GitLab token to api.github.com and get a 401. Same workspace-vs-session
    // split that has produced several bugs in this flow.
    if ((session?.user?.provider ?? '').toLowerCase() !== 'github') return;
    if (!sessionAccessToken || linkedGitOrgs.length < 2) return;
    let cancelled = false;
    searchGithubOrgs('', sessionAccessToken)
      .then((orgs) => {
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const org of orgs) {
          if (org.id) map[org.login.toLowerCase()] = org.id;
        }
        setEligibleOrgIds(map);
      })
      .catch(() => {
        /* falls back to the workspace default target */
      });
    return () => {
      cancelled = true;
    };
  }, [
    sessionAccessToken,
    session?.user?.provider,
    linkedGitOrgs.length,
    isGitlabProvider,
    isBitbucketProvider,
  ]);

  /**
   * The GitHub account id to preselect for a given org login, or undefined
   * when it cannot be resolved.
   *
   * The org's own stored id first: it comes from the backend, so it resolves
   * without the session holding an OAuth token wide enough to list orgs, which
   * eligibleOrgIds depends on. Returning undefined deliberately means "let
   * GitHub show its account picker" -- never a fallback to another org, which
   * would open a real repo-selection screen for an org the caller did not ask
   * for and read as success.
   */
  const resolveInstallTargetId = useCallback(
    (orgLogin?: string) => {
      const login = (orgLogin ?? '').toLowerCase();
      if (!login) return undefined;
      return (
        installOrgTargets.find((t) => t.login.toLowerCase() === login)
          ?.gitOrgId ?? eligibleOrgIds[login]?.toString()
      );
    },
    [installOrgTargets, eligibleOrgIds]
  );

  // Bitbucket App Password token state (shared hook)
  const isBitbucketUser = session?.user?.provider === 'bitbucket';
  const {
    hasToken: hasBitbucketToken,
    showModal: showBitbucketTokenModal,
    setShowModal: setShowBitbucketTokenModal,
    onTokenSaved: onBitbucketTokenSaved,
  } = useBitbucketOrgToken(organization?.OrgUUID ?? '', isBitbucketUser);

  // Initialize toast manager
  useToastManager();

  // Initialize GitHub permissions hook
  const { validateAndProceed } = useGitHubPermissions({
    requiredScopes: ['read:org'],
    enabled: true,
  });

  // Map unified repositories to component format
  const repositories: Repository[] = useMemo(() => {
    return unifiedRepositories
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((repo) => ({
        name: repo.name,
        description: repo.summary || '',
        status: 'Completed',
        prReviewBot: repo.isPRBotEnabled,
        // Use actual flag values when PR bot is installed; all off when no installation
        enableAutoComments: repo.hasPRBot
          ? (repo.enableAutoComments ?? false)
          : false,
        enableDocumentation: repo.hasPRBot
          ? (repo.enableDocumentation ?? false)
          : false,
        enableSecurity: repo.hasPRBot ? (repo.enableSecurity ?? false) : false,
        enableDedup: repo.hasPRBot ? (repo.enableDedup ?? false) : false,
        autoApproveOnSafe: repo.hasPRBot
          ? (repo.autoApproveOnSafe ?? false)
          : false,
        // Deliberately NOT gated on hasPRBot — agent insights are collected for
        // every repo connected to the org, whether or not the PR bot is
        // installed on it.
        agentInsightsEnabled: repo.agentInsightsEnabled ?? true,
        hasPRBot: repo.hasPRBot,
        issueBot: false,
        lastSync: '',
        dependencies: repo.dependencies,
        isBidirectional: repo.isBidirectional,
        repoUUID: repo.repoUUID,
        url: repo.url,
      }));
  }, [unifiedRepositories]);

  // Server-side search via useRepositoriesPage; rows on the current page are
  // already filtered. Kept under the same name to minimize churn below.
  const filteredRepositories = repositories;

  // Header "select all" state: checked only when every filtered repo is selected
  const headerSelectAllState = useMemo((): boolean | 'indeterminate' => {
    const total = filteredRepositories.length;
    if (total === 0) return false;
    const selectedCount = filteredRepositories.filter((r) =>
      selectedRepos.has(r.name)
    ).length;
    if (selectedCount === 0) return false;
    if (selectedCount === total) return true;
    return 'indeterminate';
  }, [filteredRepositories, selectedRepos]);

  /**
   * Recovery path when a toggle fails because the repo has no PR bot
   * installation. Sends the user somewhere that can actually fix it instead of
   * an error toast they can do nothing with: GitHub goes to the App install
   * flow, GitLab installs silently and replays the write, Bitbucket opens the
   * credentials modal.
   *
   * @param repoName repo the toggle was for
   * @param label human name of the setting, used in the retry success toast
   * @param retry re-applies the original write after a silent GitLab install;
   *   resolves true when the setting landed
   * @returns true when this took over the failure, false when the caller should
   *   show its own error toast
   */
  const recoverMissingInstallation = useCallback(
    async (
      repoName: string,
      label: string,
      retry: () => Promise<boolean>
    ): Promise<boolean> => {
      // A repo outside the workspace's git org can never be installed here:
      // the install page only offers this org's account, and the backend
      // rejects an installation from anywhere else. Say so instead of
      // sending the user on a round trip that cannot succeed. Legacy rows
      // from before the scope existed are exactly how this happens.
      if (!isRepoInOrgScope(repoName, connectedGitOrgNames)) {
        toast.show({
          title: 'Repository is outside your connected org',
          description: `${repoName} belongs to ${repoName.split('/')[0]}, and this workspace only connects repositories from ${connectedGitOrgNames || 'its connected git org'}. Ask an admin to connect that org first.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return true;
      }

      const repo = unifiedRepositories.find((r) => r.name === repoName);

      // Provider comes from the workspace (loggedOrg.Source), the same value the
      // listing and filters use, not the auth session: the install target is a
      // property of the workspace, and a member can be signed in with a
      // different identity than the org's git provider.
      if (!isGitlabProvider && !isBitbucketProvider) {
        // Say why the page is about to change, then send the user to the App
        // install flow (same destination as Add Repo). The navigation is NOT
        // wrapped in validateAndProceed: that helper returns without running its
        // callback while the scope check is loading or errored, which would
        // leave the toggle looking dead. Installing the App does not depend on
        // the OAuth repo scope anyway.
        toast.show({
          title: `${label} needs the PR bot on this repo`,
          description: `${repoName} is not connected to the Entelligence app yet. Taking you to GitHub to add it.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        window.setTimeout(() => {
          // repo_sync=1 so the return trip refreshes the listing: the install
          // webhook writes the new repo rows asynchronously, and without the
          // sync the user lands back on a table that still shows the repo as
          // unconnected.
          // The repo's OWN owner org, not the view's default target. This
          // recovery path runs for a specific repository, and with several
          // orgs linked the default is another org entirely -- sending the
          // caller there offers a repo-selection screen that cannot contain
          // the repo they were trying to fix.
          window.location.href = githubInstallUrl(
            '/settings?tab=code-management&repo_sync=1',
            resolveInstallTargetId(repoName.split('/')[0])
          );
        }, 1500);
        return true;
      }

      if (isGitlabProvider) {
        // For GitLab: attempt silent install then replay the write, fall back
        // to the modal so the user can supply a Group Access Token.
        if (repo?.url && user?.UserUUID && organization?.OrgUUID) {
          try {
            const installResult = await configurePRBotForOrgGitlab({
              UserUUID: user.UserUUID,
              RepoUrl: repo.url,
              OrgUUID: organization.OrgUUID,
            });
            if (!installResult?.error) {
              if (await retry()) {
                refetchAndSweepRows();
                toast.show({
                  title: 'Success',
                  description: `${label} updated for ${repoName}`,
                  status: 'success',
                  duration: 4000,
                  isClosable: true,
                });
                return true;
              }
              // Install succeeded but the write failed: surface that, not the
              // GAT modal, because credentials are not the problem.
              toast.show({
                title: `${label} did not turn on`,
                description: `The PR bot is set up on ${repoName} now, but the setting did not save. Please try the toggle again.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
              return true;
            }
          } catch {
            // fall through to modal
          }
          setProviderModal({
            provider: 'gitlab',
            repoUrl: repo.url,
            isLoading: false,
          });
          return true;
        }
        toast.show({
          title: 'Repository details are missing',
          description: `We could not find the connection details for ${repoName}. Hit Sync to refresh your repositories, then try again.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return true;
      }

      // For Bitbucket, open the provider modal
      if (repo?.url) {
        setProviderModal({
          provider: 'bitbucket',
          repoUrl: repo.url,
          isLoading: false,
        });
        return true;
      }
      toast.show({
        title: 'Repository details are missing',
        description: `We could not find the connection details for ${repoName}. Hit Sync to refresh your repositories, then try again.`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return true;
    },
    [
      resolveInstallTargetId,
      isBitbucketProvider,
      isGitlabProvider,
      connectedGitOrgNames,
      organization,
      refetchAndSweepRows,
      unifiedRepositories,
      user,
    ]
  );

  /**
   * "Add Repo" on a row with no PR bot installation. Same destinations as the
   * header Add Repo button, scoped to one repo: GitHub goes to the App install
   * flow, GitLab and Bitbucket open the provider modal with this repo's URL
   * prefilled. Unlike recoverMissingInstallation this is not a failure path, so
   * it carries no "setting did not save" framing.
   */
  const handleAddRepoRow = useCallback(
    (repo: Repository) => {
      // A repo outside the workspace's git org can never be installed here, so
      // say so instead of a round trip that cannot succeed.
      if (!isRepoInOrgScope(repo.name, connectedGitOrgNames)) {
        toast.show({
          title: 'Repository is outside your connected org',
          description: `${repo.name} belongs to ${repo.name.split('/')[0]}, and this workspace only connects repositories from ${connectedGitOrgNames || 'its connected git org'}. Ask an admin to connect that org first.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (isGitlabProvider || isBitbucketProvider) {
        setProviderModal({
          provider: isGitlabProvider ? 'gitlab' : 'bitbucket',
          repoUrl: repo.url,
          isLoading: false,
        });
        return;
      }

      // GitHub's install page takes no per-repo preselect, so the dialog names
      // the repo the user clicked and the navigation happens on its confirm.
      setInstallModal({ repoName: repo.name });
    },
    [isBitbucketProvider, isGitlabProvider, connectedGitOrgNames]
  );

  /**
   * Leave for the GitHub App install page. repo_sync=1 so the return trip
   * refreshes the listing: the install webhook writes the new repo rows
   * asynchronously, and without the sync the user lands back on a table that
   * still shows the repo as unconnected.
   */
  const goToGithubInstall = useCallback(
    (targetId?: string, targetOrgLogin?: string) => {
      // An explicit target wins over the workspace's first git org. Without
      // this, linking a second org and continuing to install landed on the
      // FIRST org's installation page: a real repo-selection screen for the
      // wrong org, which reads as success.
      // A pick in the dialog beats the id implied by the URL. Both are present
      // when someone arrives from linking org A and then selects org B in the
      // picker, and honouring the URL there would send them to A while the
      // dialog said B — the same wrong-org-that-looks-right this flow keeps
      // producing.
      // The org's own stored account id first: it comes from the backend, so
      // it resolves without the session holding a GitHub OAuth token wide
      // enough to list orgs, which eligibleOrgIds depends on.
      const returnTo = '/settings?tab=code-management&repo_sync=1';
      const login = (targetOrgLogin ?? '').toLowerCase();
      const pickedId = resolveInstallTargetId(login);

      if (login) {
        // An org was explicitly chosen. Its own resolved id wins.
        //
        // When that does not resolve -- combining info may still be loading,
        // which is exactly the case for an install prompt arriving with
        // target_id/target_org in the URL -- keep the id the URL carried
        // rather than discarding it: it came from the same flow that named
        // this org, and dropping it strands the caller on an account picker
        // where they can install into the wrong account. Only ignore it when
        // it demonstrably belongs to a DIFFERENT linked org, which is the one
        // case where honouring it would contradict the caller's pick.
        //
        // Never buildInstallUrl here, whose default is the home account:
        // opening a real repo-selection screen for an org the caller did not
        // choose reads as success.
        const urlTargetIsAnotherOrg = installOrgTargets.some(
          (t) =>
            t.gitOrgId &&
            t.gitOrgId === targetId &&
            t.login.toLowerCase() !== login
        );
        window.location.href = githubInstallUrl(
          returnTo,
          pickedId ?? (urlTargetIsAnotherOrg ? undefined : targetId)
        );
        return;
      }

      const resolvedId = pickedId ?? targetId;
      window.location.href = resolvedId
        ? githubInstallUrl(returnTo, resolvedId)
        : buildInstallUrl(returnTo);
    },
    [buildInstallUrl, resolveInstallTargetId, installOrgTargets]
  );

  // Toggle PR bot enabled handler
  const handleTogglePRBot = useCallback(
    async (repoName: string) => {
      if (!organization?.OrgUUID) {
        toast.show({
          title: 'Error',
          description: 'Organization not found',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (togglingRepos.has(repoName)) return;

      try {
        setTogglingRepos((prev) => new Set(prev).add(repoName));

        const result = await togglePRBotEnabled({
          repoName,
          orgUUID: organization.OrgUUID,
        });

        if (result.success) {
          // Refetch repositories to get updated status
          refetchAndSweepRows();

          toast.show({
            title: 'Success',
            description: `PR evaluation ${result.enable_pr_evaluation ? 'enabled' : 'disabled'} for ${repoName}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });

          posthog.capture('pr_bot_toggled', {
            repo_name: repoName,
            enable_pr_evaluation: result.enable_pr_evaluation,
            username: session?.user?.GithubUsername,
          });
        } else {
          throw new Error(result.error || 'Failed to toggle PR bot');
        }
      } catch (error) {
        console.error('Failed to toggle PR evaluation:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to toggle PR bot';

        const handled =
          isMissingInstallationError(errorMessage) &&
          (await recoverMissingInstallation(
            repoName,
            'PR Evaluation',
            async () => {
              const enableResult = await togglePRBotEnabled({
                repoName,
                orgUUID: organization.OrgUUID,
                enable_pr_evaluation: true,
              });
              return enableResult.success;
            }
          ));

        if (!handled) {
          // Show regular error toast
          toast.show({
            title: 'PR Evaluation did not change',
            description: `We could not update PR Evaluation for ${repoName}. Please try again in a moment. If it keeps happening, reach out on Intercom.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        posthog.capture('pr_bot_toggle_error', {
          repo_name: repoName,
          error_message: errorMessage,
          username: session?.user?.GithubUsername,
        });
      } finally {
        setTogglingRepos((prev) => {
          const newSet = new Set(prev);
          newSet.delete(repoName);
          return newSet;
        });
      }
    },
    [
      organization,
      refetchAndSweepRows,
      togglingRepos,
      session,
      recoverMissingInstallation,
    ]
  );

  // Update a single PR bot setting (auto comments, documentation, security)
  const handleToggleSetting = useCallback(
    async (
      repo: Repository,
      field:
        'enable_auto_comments' | 'enable_documentation' | 'enable_security',
      currentValue: boolean
    ) => {
      if (!organization?.OrgUUID) return;
      const key = `${repo.name}:${field}`;
      if (togglingRepos.has(key)) return;
      try {
        setTogglingRepos((prev) => new Set(prev).add(key));
        const payload = {
          repoName: repo.name,
          orgUUID: organization.OrgUUID,
          [field]: !currentValue,
        };
        const result = await togglePRBotEnabled(payload);
        if (result.success) {
          refetchAndSweepRows();
          toast.show({
            title: 'Success',
            description: `Setting updated for ${repo.name}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(result.error ?? 'Failed to update setting');
        }
      } catch (error) {
        console.error('Failed to update repository setting:', error);
        const msg =
          error instanceof Error ? error.message : 'Failed to update setting';
        const handled =
          isMissingInstallationError(msg) &&
          (await recoverMissingInstallation(
            repo.name,
            TOGGLE_LABELS[field],
            async () => {
              const retryResult = await togglePRBotEnabled({
                repoName: repo.name,
                orgUUID: organization.OrgUUID,
                [field]: !currentValue,
              });
              return retryResult.success;
            }
          ));

        if (!handled) {
          toast.show({
            title: `${TOGGLE_LABELS[field]} did not change`,
            description: `We could not update ${TOGGLE_LABELS[field]} for ${repo.name}. Please try again in a moment. If it keeps happening, reach out on Intercom.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setTogglingRepos((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [
      organization,
      refetchAndSweepRows,
      togglingRepos,
      recoverMissingInstallation,
    ]
  );

  // Toggle dedup setting for a single repository
  const handleToggleDedup = useCallback(
    async (repo: Repository) => {
      if (!organization?.OrgUUID) return;
      const key = `${repo.name}:dedup`;
      if (togglingRepos.has(key)) return;
      try {
        setTogglingRepos((prev) => new Set(prev).add(key));
        const result = await toggleDedup({
          repoName: repo.name,
          orgUUID: organization.OrgUUID,
          enable_dedup: !repo.enableDedup,
        });
        if (result.success) {
          refetchRepositories();
          toast.show({
            title: 'Success',
            description: `Deduplication ${!repo.enableDedup ? 'enabled' : 'disabled'} for ${repo.name}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(result.error ?? 'Failed to update dedup setting');
        }
      } catch (error) {
        console.error('Failed to update deduplication setting:', error);
        const msg =
          error instanceof Error
            ? error.message
            : 'Failed to update dedup setting';

        const handled =
          isMissingInstallationError(msg) &&
          (await recoverMissingInstallation(
            repo.name,
            'Deduplication',
            async () => {
              const retryResult = await toggleDedup({
                repoName: repo.name,
                orgUUID: organization.OrgUUID,
                enable_dedup: !repo.enableDedup,
              });
              return retryResult.success;
            }
          ));

        if (!handled) {
          toast.show({
            title: 'Deduplication did not change',
            description: `We could not update Deduplication for ${repo.name}. Please try again in a moment. If it keeps happening, reach out on Intercom.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setTogglingRepos((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [
      organization,
      refetchRepositories,
      togglingRepos,
      recoverMissingInstallation,
    ]
  );

  const handleToggleAutoApprove = useCallback(
    async (repo: Repository) => {
      if (!organization?.OrgUUID) return;
      const key = `${repo.name}:autoapprove`;
      if (togglingRepos.has(key)) return;
      try {
        setTogglingRepos((prev) => new Set(prev).add(key));
        const result = await toggleAutoApprove({
          repoName: repo.name,
          orgUUID: organization.OrgUUID,
          auto_approve_on_safe: !repo.autoApproveOnSafe,
        });
        if (result.success) {
          refetchRepositories();
          toast.show({
            title: 'Success',
            description: `Auto-approve at 5/5 ${!repo.autoApproveOnSafe ? 'enabled' : 'disabled'} for ${repo.name}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(
            result.error ?? 'Failed to update auto-approve setting'
          );
        }
      } catch (error) {
        console.error('Failed to update auto-approve setting:', error);
        const msg =
          error instanceof Error
            ? error.message
            : 'Failed to update auto-approve setting';

        const handled =
          isMissingInstallationError(msg) &&
          (await recoverMissingInstallation(
            repo.name,
            'Auto-approve at 5/5',
            async () => {
              const retryResult = await toggleAutoApprove({
                repoName: repo.name,
                orgUUID: organization.OrgUUID,
                auto_approve_on_safe: !repo.autoApproveOnSafe,
              });
              return retryResult.success;
            }
          ));

        if (!handled) {
          toast.show({
            title: 'Auto-approve did not change',
            description: `We could not update Auto-approve at 5/5 for ${repo.name}. Please try again in a moment. If it keeps happening, reach out on Intercom.`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setTogglingRepos((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [
      organization,
      refetchRepositories,
      togglingRepos,
      recoverMissingInstallation,
    ]
  );

  // Toggle agent-insights collection for a single repository (ENT-3725).
  const handleToggleAgentInsights = useCallback(
    async (repo: Repository) => {
      if (!organization?.OrgUUID) return;
      const key = `${repo.name}:agentinsights`;
      if (togglingRepos.has(key)) return;
      const next = !repo.agentInsightsEnabled;
      try {
        setTogglingRepos((prev) => new Set(prev).add(key));
        const result = await setAgentInsightsRepoCollection({
          orgUUID: organization.OrgUUID,
          repoNames: [repo.name],
          enabled: next,
        });
        if (result.success) {
          refetchRepositories();
          toast.show({
            title: 'Success',
            description: next
              ? `Now collecting agent insights for ${repo.name}`
              : `Stopped collecting agent insights for ${repo.name}. Existing insights are kept.`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(
            result.error ?? 'Failed to update agent insights setting'
          );
        }
      } catch (error) {
        console.error('Failed to update agent insights setting:', error);
        toast.show({
          title: 'Error',
          description:
            "Couldn't update agent insights for this repository. Please try again.",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setTogglingRepos((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(key);
          return nextSet;
        });
      }
    },
    [organization, refetchRepositories, togglingRepos]
  );

  /**
   * Apply a flag change to every repo matching the current filter.
   *
   * Sends the filter rather than a list of names, so the scope is not limited
   * to the rows that happen to be loaded. The server applies it in one
   * statement and reports how many rows it touched.
   */
  const applyToEveryMatchingRepo = useCallback(
    async (
      flags: {
        enable_pr_evaluation?: boolean;
        enable_auto_comments?: boolean;
        enable_dedup?: boolean;
        auto_approve_on_safe?: boolean;
      },
      label: string
    ) => {
      if (!organization?.OrgUUID) return;
      const scope = {
        orgUUID: organization.OrgUUID,
        // Repo names are "owner/repo" on GitHub and "group/repo" on GitLab, so
        // the backend's owner-prefix match covers both; whichever filter the
        // provider uses is the same narrowing.
        git_org: selectedGitOrg || selectedGitlabGroupPath || undefined,
        search: debouncedSearchTerm.trim() || undefined,
      };
      try {
        const result = await updatePRBotSettingsForOrg({ ...scope, ...flags });
        if (!result.success) throw new Error(result.error ?? 'update failed');
        setApplyToAllMatching(false);
        setSelectedRepos(new Set());
        await refetchRepositories();
        toast.show({
          title: 'Success',
          description: `${label} for ${result.updated_count} repositor${
            result.updated_count === 1 ? 'y' : 'ies'
          }.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Bulk repo settings update failed:', error);
        toast.show({
          title: 'Error',
          description:
            "Couldn't update these repositories. Please try again in a moment.",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [
      organization,
      selectedGitOrg,
      selectedGitlabGroupPath,
      debouncedSearchTerm,
      refetchRepositories,
      setSelectedRepos,
    ]
  );

  // Bulk-set agent-insights collection for every selected repository.
  // Unlike the sibling bulk toggles — which fire one request per repo — this
  // sends a single batched request, so "search a prefix → select all → toggle"
  // stays one call even at several hundred repos.
  const handleBulkAgentInsights = useCallback(
    async (repoNames: string[], enabled: boolean) => {
      if (!organization?.OrgUUID || repoNames.length === 0) return;
      try {
        const result = await setAgentInsightsRepoCollection({
          orgUUID: organization.OrgUUID,
          repoNames,
          enabled,
        });
        if (result.success) {
          refetchRepositories();
          toast.show({
            title: 'Success',
            description: enabled
              ? `Now collecting agent insights for ${repoNames.length} repositories`
              : `Stopped collecting agent insights for ${repoNames.length} repositories. Existing insights are kept.`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(
            result.error ?? 'Failed to update agent insights setting'
          );
        }
      } catch (error) {
        console.error('Failed to bulk update agent insights setting:', error);
        toast.show({
          title: 'Error',
          description:
            "Couldn't update agent insights for these repositories. Please try again.",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [organization, refetchRepositories]
  );

  // Org-level agent-insights default (ENT-3725 D2) — applies to every repo with
  // no explicit per-repo setting, including repos that don't exist yet. That
  // last part is why this control exists at all: a per-repo switch can't hold
  // an opinion about a repo nobody has created.
  const [isSavingOrgDefault, setIsSavingOrgDefault] = useState(false);
  const handleToggleAgentInsightsOrgDefault = useCallback(
    async (next: boolean) => {
      if (!organization?.OrgUUID || isSavingOrgDefault) return;
      try {
        setIsSavingOrgDefault(true);
        const result = await setAgentInsightsRepoCollection({
          orgUUID: organization.OrgUUID,
          orgDefault: next,
        });
        if (result.success) {
          refetchRepositories();
          toast.show({
            title: 'Success',
            description: next
              ? 'New repositories will collect agent insights by default'
              : 'New repositories will not collect agent insights by default',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          throw new Error(result.error ?? 'Failed to update the default');
        }
      } catch (error) {
        console.error('Failed to update agent insights org default:', error);
        toast.show({
          title: 'Error',
          description:
            "Couldn't update the default for agent insights. Please try again.",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setIsSavingOrgDefault(false);
      }
    },
    [organization, refetchRepositories, isSavingOrgDefault]
  );

  const openConfirmModal = useCallback(
    (repo: Repository, field: ToggleField, currentValue: boolean) => {
      setConfirmToggle({ repo, field, currentValue });
    },
    []
  );

  const handleConfirmToggle = useCallback(() => {
    if (!confirmToggle) return;
    const { repo, field, currentValue } = confirmToggle;
    if (field === 'pr_evaluation') {
      handleTogglePRBot(repo.name);
    } else {
      handleToggleSetting(repo, field, currentValue);
    }
    setConfirmToggle(null);
  }, [confirmToggle, handleTogglePRBot, handleToggleSetting]);

  // Repos that currently have PR Review (auto comments) or PR Evaluation enabled
  const reposWithPRReviewEnabled = useMemo(
    () => repositories.filter((r) => r.enableAutoComments).map((r) => r.name),
    [repositories]
  );
  const reposWithPREvaluationEnabled = useMemo(
    () => repositories.filter((r) => r.prReviewBot).map((r) => r.name),
    [repositories]
  );

  const handleMultiSelectDisable = useCallback(
    async (selectedRepoNames: string[]) => {
      if (!multiSelectModal || !organization?.OrgUUID) return;
      const { field } = multiSelectModal;
      setMultiSelectModal(null);

      const isPREvaluation = field === 'pr_evaluation';
      const label = isPREvaluation ? 'PR Evaluation' : 'PR Review';
      const errors: string[] = [];

      // Same keys the row switches read, so they show as pending for the whole
      // write. Without this the switch sits at its old value until the refetch
      // lands, which is what made the result look like a bounce-back.
      const pendingKeys = selectedRepoNames.map((repoName) =>
        isPREvaluation ? repoName : `${repoName}:enable_auto_comments`
      );
      setTogglingRepos((prev) => {
        const next = new Set(prev);
        pendingKeys.forEach((k) => next.add(k));
        return next;
      });

      for (const repoName of selectedRepoNames) {
        try {
          const result = await togglePRBotEnabled({
            repoName,
            orgUUID: organization.OrgUUID,
            ...(isPREvaluation
              ? { enable_pr_evaluation: false }
              : { enable_auto_comments: false }),
          });
          if (!result.success && result.error) {
            errors.push(`${repoName}: ${result.error}`);
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unknown error';
          errors.push(`${repoName}: ${msg}`);
        }
      }

      await refetchAndSweepRows();

      setTogglingRepos((prev) => {
        const next = new Set(prev);
        pendingKeys.forEach((k) => next.delete(k));
        return next;
      });

      if (errors.length > 0) {
        console.error(`Failed to disable ${label} for some repos:`, errors);
        toast.show({
          title: 'Partially failed',
          description: `Failed to update ${errors.length} repositor${errors.length === 1 ? 'y' : 'ies'}. Please try again.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const count = selectedRepoNames.length;
        toast.show({
          title: 'Success',
          description: `${label} disabled for ${count} ${count === 1 ? 'repo' : 'repos'}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [multiSelectModal, organization?.OrgUUID, refetchAndSweepRows]
  );

  // Bulk-enable PR Review across selected repos.
  // For GitLab, installing the webhook per repo via configurePRBotForOrgGitlab is the equivalent
  // of the per-repo single install — toggling enable_auto_comments alone fails when the PR bot
  // isn't installed yet. For other providers we keep the lighter toggle path.
  const handleMultiSelectEnablePRReview = useCallback(
    async (selectedRepoNames: string[]) => {
      if (!organization?.OrgUUID || !user?.UserUUID) return;
      const provider = session?.user?.provider;
      const isGitlab = provider === 'gitlab';
      const isBitbucket = provider === 'bitbucket';
      const orgUUID = organization.OrgUUID;
      const errors: string[] = [];

      const targets = selectedRepoNames
        .map((name) => repositories.find((r) => r.name === name))
        .filter((r): r is Repository => Boolean(r));

      if (targets.length === 0) return;

      setBulkEnableProgress({ current: 0, total: targets.length });
      // Track successes explicitly rather than deriving from errors.length —
      // a single top-level error (e.g. auth failure) represents N failed
      // targets, not 1, so subtracting would inflate the success toast.
      let bitbucketBulkSuccesses: number | null = null;

      try {
        if (isBitbucket) {
          try {
            const response = await configurePRBotForOrgBitbucketBulk({
              UserUUID: user.UserUUID,
              OrgUUID: orgUUID,
              Items: targets.map((r) => ({ RepoUrl: r.url, Enable: true })),
              AccessToken:
                (user?.accessToken || session?.user?.accessToken) ?? '',
            });
            const rows = response?.Results ?? [];
            let rowSuccesses = 0;
            for (const row of rows) {
              if (row.Error) {
                const match = targets.find((r) => r.url === row.RepoUrl);
                errors.push(`${match?.name ?? row.RepoUrl}: ${row.Error}`);
              } else {
                rowSuccesses += 1;
              }
            }
            if (response?.error) {
              // Top-level failure — anything not explicitly succeeded counts
              // as failed. Surface the cause without double-listing rows we
              // already attributed above.
              errors.push(response.error);
            }
            bitbucketBulkSuccesses = rowSuccesses;
            setBulkEnableProgress({
              current: targets.length,
              total: targets.length,
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Unknown error';
            errors.push(msg);
            bitbucketBulkSuccesses = 0;
          }
        } else {
          // GitLab needs one webhook install per repo and the toggle path has no
          // batch endpoint that also reports per-repo failures, so this stays a
          // request per repo. It runs through a bounded pool rather than a
          // sequential loop: 200 selected repos used to mean 200 round trips
          // end to end. The cap is what keeps that from turning into 200
          // simultaneous calls against a provider that rate-limits per token.
          const enableOne = async (repo: Repository) => {
            try {
              if (isGitlab) {
                const response = await configurePRBotForOrgGitlab({
                  UserUUID: user.UserUUID,
                  RepoUrl: repo.url,
                  OrgUUID: orgUUID,
                });
                if (response?.error) {
                  errors.push(`${repo.name}: ${response.error}`);
                }
              } else {
                const result = await togglePRBotEnabled({
                  repoName: repo.name,
                  orgUUID,
                  enable_auto_comments: true,
                });
                if (!result.success && result.error) {
                  errors.push(`${repo.name}: ${result.error}`);
                }
              }
            } catch (err) {
              const msg = err instanceof Error ? err.message : 'Unknown error';
              errors.push(`${repo.name}: ${msg}`);
            }
          };

          // Counts completions, not starts: with a pool, "current" has to mean
          // repos actually done or the bar reads ahead of the work.
          let cursor = 0;
          let completed = 0;
          const worker = async () => {
            while (cursor < targets.length) {
              const repo = targets[cursor];
              cursor += 1;
              await enableOne(repo);
              completed += 1;
              setBulkEnableProgress({
                current: completed,
                total: targets.length,
              });
            }
          };
          await Promise.all(
            Array.from(
              { length: Math.min(BULK_ENABLE_CONCURRENCY, targets.length) },
              worker
            )
          );
        }

        await queryClient.invalidateQueries({
          queryKey: unifiedCodebaseQueryKeys.all,
        });
        await queryClient.invalidateQueries({
          queryKey: codebaseReposQueryKeys.all,
        });
        await refetchRepositories();
      } finally {
        setBulkEnableProgress(null);
      }

      const successCount =
        bitbucketBulkSuccesses !== null
          ? bitbucketBulkSuccesses
          : Math.max(0, targets.length - errors.length);
      if (errors.length > 0) {
        console.error('Bulk PR Review enable failures:', errors);
        toast.show({
          title: 'Partially failed',
          description: `PR Review enabled for ${successCount} repo(s). ${errors.length} failed — please try again.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (successCount > 0) {
        toast.show({
          title: 'Success',
          description: `PR Review enabled for ${successCount} ${successCount === 1 ? 'repo' : 'repos'}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [
      organization?.OrgUUID,
      user?.UserUUID,
      session?.user?.provider,
      session?.user?.accessToken,
      user?.accessToken,
      repositories,
      queryClient,
      refetchRepositories,
    ]
  );

  // Set up new repository with GitHub permissions validation
  const setUpNewRepo = () => {
    if (isSyncInProgress) return;
    if (session?.user?.provider === 'github') {
      // validateAndProceed stays on the click, not on the dialog's confirm, so
      // a missing OAuth repo scope still opens PermissionInfoModal instead of
      // sending the user to an install that cannot list their repos.
      validateAndProceed(() => {
        setInstallModal({});
      });
    } else if (session?.user?.provider === 'bitbucket') {
      setProviderModal({
        provider: 'bitbucket',
        repoUrl: '',
        isLoading: false,
      });
    } else {
      setProviderModal({ provider: 'gitlab', repoUrl: '', isLoading: false });
    }
  };

  // Unified webhook installation for GitLab and Bitbucket
  const installProviderWebhook = async () => {
    if (
      !providerModal?.repoUrl ||
      !user?.UserUUID ||
      (!organization?.OrgUUID && !user?.OrganizationUUID)
    )
      return;

    const { provider, repoUrl, groupAccessToken: gat } = providerModal;

    if (provider === 'bitbucket') {
      // Gate on Bitbucket App Password — show token modal if not configured.
      // Store the repo URL so we can resume after the modal succeeds without
      // relying on a stale closure that still sees hasBitbucketToken === false.
      if (!hasBitbucketToken) {
        pendingBitbucketRepoUrlRef.current = repoUrl;
        setShowBitbucketTokenModal(true);
        return;
      }

      const accessToken = user?.accessToken || session?.user?.accessToken;
      if (!accessToken) {
        toast.show({
          title: 'Error',
          description:
            'No Bitbucket access token available. Please re-authenticate.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    // The backend skips out-of-scope repos silently, so say it here rather than
    // letting the user watch a successful-looking request do nothing.
    if (!isRepoInOrgScope(repoPathFromUrl(repoUrl), connectedGitOrgNames)) {
      toast.show({
        title: 'Outside your connected org',
        description: `This workspace connects repositories from ${connectedGitOrgNames || 'its connected git org'} only.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setProviderModal((prev) => (prev ? { ...prev, isLoading: true } : prev));
    try {
      const orgUUID = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
      const response =
        provider === 'gitlab'
          ? await configurePRBotForOrgGitlab({
              UserUUID: user?.UserUUID ?? '',
              RepoUrl: repoUrl,
              OrgUUID: orgUUID,
              GroupAccessToken: gat ?? '',
            })
          : await configurePRBotForOrgBitbucket({
              UserUUID: user?.UserUUID ?? '',
              RepoUrl: repoUrl,
              OrgUUID: orgUUID,
              AccessToken:
                (user?.accessToken || session?.user?.accessToken) ?? '',
            });

      if (response?.error) {
        console.error(
          'Failed to configure repository webhook:',
          response.error
        );
        toast.show({
          title: 'Error',
          description:
            "Couldn't set up the webhook for this repository. Please try again.",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const label =
          provider === 'gitlab' ? 'Repository' : 'Bitbucket repository';
        toast.show({
          title: 'Success',
          description: `${label} configured successfully`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setProviderModal(null);

        // Invalidate all repo-listing queries (both roots during the migration)
        // so the switch reflects the newly-configured PR bot state.
        await queryClient.invalidateQueries({
          queryKey: unifiedCodebaseQueryKeys.all,
        });
        await queryClient.invalidateQueries({
          queryKey: codebaseReposQueryKeys.all,
        });
      }
    } catch {
      const label = provider === 'gitlab' ? '' : ' Bitbucket';
      toast.show({
        title: 'Error',
        description: `Failed to configure${label} repository`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProviderModal((prev) => (prev ? { ...prev, isLoading: false } : null));
    }
  };

  /**
   * Resume Bitbucket repo configuration after the user saves their App Password.
   * Called directly from onSuccess — avoids the stale-closure problem of
   * replaying `installProviderWebhook` where `hasBitbucketToken` is still false.
   */
  const proceedWithBitbucketConfig = async (repoUrl: string) => {
    if (!user?.UserUUID || (!organization?.OrgUUID && !user?.OrganizationUUID))
      return;
    const accessToken = user?.accessToken || session?.user?.accessToken;
    if (!accessToken) {
      toast.show({
        title: 'Error',
        description:
          'No Bitbucket access token available. Please re-authenticate.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    // The backend skips out-of-scope repos silently, so say it here rather than
    // letting the user watch a successful-looking request do nothing.
    if (!isRepoInOrgScope(repoPathFromUrl(repoUrl), connectedGitOrgNames)) {
      toast.show({
        title: 'Outside your connected org',
        description: `This workspace connects repositories from ${connectedGitOrgNames || 'its connected git org'} only.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setProviderModal((prev) => (prev ? { ...prev, isLoading: true } : prev));
    try {
      const orgUUID = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
      const response = await configurePRBotForOrgBitbucket({
        UserUUID: user.UserUUID,
        RepoUrl: repoUrl,
        OrgUUID: orgUUID,
        AccessToken: accessToken,
      });
      if (response?.error) {
        console.error('Failed to configure Bitbucket webhook:', response.error);
        toast.show({
          title: 'Error',
          description:
            "Couldn't set up the webhook for this repository. Please try again.",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast.show({
          title: 'Success',
          description: 'Bitbucket repository configured successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setProviderModal(null);
        await queryClient.invalidateQueries({
          queryKey: unifiedCodebaseQueryKeys.all,
        });
        await queryClient.invalidateQueries({
          queryKey: codebaseReposQueryKeys.all,
        });
      }
    } catch {
      toast.show({
        title: 'Error',
        description: 'Failed to configure Bitbucket repository',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProviderModal((prev) => (prev ? { ...prev, isLoading: false } : null));
    }
  };

  return (
    <div className={className}>
      <div className="space-y-6 motion-stagger">
        {/* Header */}
        <SettingsHeader
          title="Codebase management"
          description="Manage all your repositories for the selected organization"
        />

        {/* Org-level spend-attribution mode (Agent Insights) */}
        {organization?.OrgUUID && (
          <AttributionSettingsCard
            orgUUID={organization.OrgUUID}
            isAdmin={isAdminOrOwner(loggedUser?.Role)}
          />
        )}

        {/* Search and Add Button */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 max-w-64">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-ds-text-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 border border-ds-border-subtlest rounded-md bg-ds-surface-white text-ds-text-darker text-sm focus:outline-none focus:ring-2 focus:ring-ds-brand-subtler focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Explicit entry point for "configure everything at once".
                The header checkbox can do it, but a checkbox in a table header
                does not read as an action, so on a 243-repo org the bulk path
                was effectively undiscoverable. This states the scope and the
                count up front, next to the filter that defines it. */}
            {/* Not offered while a Bitbucket project filter is active: a
                project key is not a repo-name prefix, so the server has no way
                to express that narrowing and "select all" would silently mean
                every repo in the org. */}
            {reposTotalCount > 0 && !selectedBitbucketProjectKey && (
              <ShadcnButton
                variant="outline"
                size="sm"
                onClick={() => {
                  if (applyToAllMatching) {
                    clearSelection();
                  } else {
                    setApplyToAllMatching(true);
                    setSelectedRepos(
                      new Set(filteredRepositories.map((r) => r.name))
                    );
                  }
                }}
              >
                {applyToAllMatching
                  ? 'Clear selection'
                  : `Select all ${reposTotalCount} ${
                      reposTotalCount === 1 ? 'repo' : 'repos'
                    }`}
              </ShadcnButton>
            )}

            {isBitbucketProvider && (
              <div className="w-56">
                <BitbucketProjectCombobox
                  projects={bitbucketProjects}
                  isLoading={isLoadingBitbucketProjects}
                  selectedKey={selectedBitbucketProjectKey}
                  onSelect={(key) => setSelectedBitbucketProjectKey(key)}
                />
              </div>
            )}

            {!isGitlabProvider &&
              !isBitbucketProvider &&
              linkedGitOrgs.length > 1 && (
                <div className="w-64">
                  <Select
                    value={selectedGitOrg || PROVIDER_FILTER_ALL}
                    onValueChange={(v) => {
                      if (v == null) return;
                      setSelectedGitOrg(v === PROVIDER_FILTER_ALL ? '' : v);
                    }}
                    items={[
                      {
                        value: PROVIDER_FILTER_ALL,
                        label: 'All organizations',
                      },
                      ...linkedGitOrgs.map((org) => ({
                        value: org,
                        label: org,
                      })),
                    ]}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by organization" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PROVIDER_FILTER_ALL}>
                        All organizations
                      </SelectItem>
                      {linkedGitOrgs.map((org) => (
                        <SelectItem key={org} value={org}>
                          {org}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

            {isGitlabProvider && (
              <div className="w-64">
                <Select
                  value={selectedGitlabGroupPath || PROVIDER_FILTER_ALL}
                  onValueChange={(v) => {
                    if (v == null) return;
                    setSelectedGitlabGroupPath(
                      v === PROVIDER_FILTER_ALL ? '' : v
                    );
                  }}
                  disabled={isLoadingGitlabGroups}
                  items={[
                    { value: PROVIDER_FILTER_ALL, label: 'All groups' },
                    ...gitlabGroups.map((g) => ({
                      value: g.full_path || g.path,
                      label: g.full_path || g.name,
                    })),
                  ]}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isLoadingGitlabGroups
                          ? 'Loading groups…'
                          : 'Filter by GitLab group/subgroup'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PROVIDER_FILTER_ALL}>
                      All groups
                    </SelectItem>
                    {gitlabGroups.map((g) => (
                      <SelectItem
                        key={g.full_path || g.path}
                        value={g.full_path || g.path}
                      >
                        {g.full_path || g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isBitbucketProvider && (
              <SyncReposButton
                orgUUID={organization?.OrgUUID || ''}
                userUUID={user?.UserUUID || ''}
                variant="icon"
              />
            )}
            <ShadcnButton
              type="button"
              variant="default"
              size="default"
              onClick={setUpNewRepo}
              disabled={isSyncInProgress}
            >
              <Plus />
              Add Repo
            </ShadcnButton>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-ds-text-darker">
              Collect agent insights for new repositories
            </span>
            <span className="text-xs text-ds-text-subtle">
              Applies to every repository you haven&apos;t set individually,
              including ones added later. Turning this off stops future
              collection; insights already collected are kept.
            </span>
          </div>
          <Switch
            checked={agentInsightsOrgDefault}
            onCheckedChange={(checked: boolean) =>
              handleToggleAgentInsightsOrgDefault(checked)
            }
            disabled={isSavingOrgDefault || isLoadingRepositories}
            aria-label="Collect agent insights for new repositories"
            className="data-checked:bg-ds-brand-subtle"
          />
        </div>

        {reposApiError && (
          <div
            className="mb-4 px-4 py-2 text-sm text-ds-warning-default bg-ds-warning-subtlest/30 rounded-lg border border-amber-200"
            role="alert"
          >
            Couldn&apos;t load repositories. Please try again in a moment.
          </div>
        )}

        {(isSyncInProgress || isSyncFailed) && (
          <InstallationSyncBanner
            status={installationSyncStatus}
            progressData={installationSyncProgressData}
            onRetry={
              session?.user?.provider === 'github'
                ? handleRetryInstallation
                : undefined
            }
          />
        )}

        {/* Table */}
        {isLoadingRepositories ? (
          <RepositoriesTableSkeleton />
        ) : !unifiedRepositories.length || repositoriesError ? (
          <div className="w-full flex flex-col justify-center items-center p-6">
            <p className="text-ds-text-subtle text-xl mb-2">
              No repositories found
            </p>
            <p className="text-ds-text-subtle text-sm">
              {reposApiError
                ? "Couldn't load repositories. Please try again in a moment."
                : 'Try adding a repository'}
            </p>
          </div>
        ) : filteredRepositories.length === 0 ? (
          <div className="w-full flex flex-col justify-center items-center p-6">
            <p className="text-ds-text-subtle text-xl mb-2">
              No repositories found
            </p>
            <p className="text-ds-text-subtle text-sm">
              {searchTerm
                ? `No repositories matching "${searchTerm}"`
                : 'Try adding a repository'}
            </p>
          </div>
        ) : (
          <div className="relative">
            {isSyncInProgress && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-ds-surface-white/60"
                aria-hidden
              />
            )}
            <div className="bg-ds-surface-white rounded-lg border border-ds-border-subtlest overflow-hidden">
              {/* Escape from page-sized selection. The header checkbox can only
                  reach the rows the listing returned, so on a large org the
                  only way to configure everything was to page through it. This
                  offers the whole matching set in one action. */}
              {headerSelectAllState === true &&
                !selectedBitbucketProjectKey &&
                reposTotalCount > filteredRepositories.length && (
                  <div className="flex items-center justify-center gap-2 border-b border-ds-border-subtlest bg-ds-brand-lightest px-4 py-2.5">
                    <span className="ds-text-body-sm-medium text-ds-text-default">
                      {applyToAllMatching
                        ? `All ${reposTotalCount} repositories matching this filter are selected.`
                        : `All ${filteredRepositories.length} repositories on this page are selected.`}
                    </span>
                    <button
                      type="button"
                      data-no-tap
                      onClick={() => {
                        // Computed outside the setter: React may replay an
                        // updater, so a nested setState in one can run twice.
                        if (applyToAllMatching) {
                          clearSelection();
                        } else {
                          setApplyToAllMatching(true);
                        }
                      }}
                      className="ds-text-body-sm-semibold text-ds-brand-subtle underline underline-offset-2 hover:text-ds-brand-default transition-colors"
                    >
                      {applyToAllMatching
                        ? 'Clear selection'
                        : `Select all ${reposTotalCount} repositories`}
                    </button>
                  </div>
                )}
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-ds-text-subtle/30 scrollbar-track-ds-gray-subtlest/30">
                <table
                  className="w-full min-w-[1108px]"
                  style={{ tableLayout: 'fixed' }}
                >
                  <colgroup>
                    <col style={{ width: '44px' }} />
                    <col style={{ width: '280px' }} />
                    <col style={{ width: '128px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '112px' }} />
                  </colgroup>
                  <thead className="bg-ds-gray-subtlest/50 border-b border-ds-border-subtlest">
                    <tr>
                      <th
                        className="px-3 py-2 text-center align-middle"
                        style={{ width: '44px' }}
                      >
                        <Checkbox
                          checked={headerSelectAllState === true}
                          indeterminate={
                            headerSelectAllState === 'indeterminate'
                          }
                          onCheckedChange={(checked) => {
                            if (checked === true) {
                              setSelectedRepos(
                                new Set(filteredRepositories.map((r) => r.name))
                              );
                            } else {
                              clearSelection();
                            }
                          }}
                          aria-label="Select all repositories"
                          className="border-ds-border-subtlest data-checked:bg-ds-brand-subtle data-checked:border-ds-brand-subtle data-checked:text-ds-surface-white data-indeterminate:bg-ds-brand-subtle data-indeterminate:border-ds-brand-subtle data-indeterminate:text-ds-surface-white"
                        />
                      </th>
                      <th
                        className="pl-0 pr-4 py-2 text-left text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '280px',
                        }}
                      >
                        Repo
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '128px',
                        }}
                      >
                        Linked Repos
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '96px',
                        }}
                      >
                        <TooltipProvider delay={200}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <span className="cursor-default inline-flex items-center gap-1">
                                  PR Review
                                  <Info
                                    size={14}
                                    className="text-ds-text-subtle/70"
                                  />
                                </span>
                              }
                            />
                            <TooltipContent
                              side="top"
                              className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                            >
                              Enable or disable automated code review comments
                              on pull requests for this repository. For metrics
                              and scoring, see PR Evaluation.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '96px',
                        }}
                      >
                        <TooltipProvider delay={200}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <span className="cursor-default inline-flex items-center gap-1">
                                  PR Evaluation
                                  <Info
                                    size={14}
                                    className="text-ds-text-subtle/70"
                                  />
                                </span>
                              }
                            />
                            <TooltipContent
                              side="top"
                              className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                            >
                              Enable or disable PR evaluation for this
                              repository. Required for Team Insights such as
                              reviews, metrics, and scoring.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '96px',
                        }}
                      >
                        <TooltipProvider delay={200}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <span className="cursor-default inline-flex items-center gap-1">
                                  Deduplication
                                  <Info
                                    size={14}
                                    className="text-ds-text-subtle/70"
                                  />
                                </span>
                              }
                            />
                            <TooltipContent
                              side="top"
                              className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                            >
                              Enable or disable deduplication of PR reviews.
                              When enabled, duplicate review comments are
                              suppressed.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '96px',
                        }}
                      >
                        <TooltipProvider delay={200}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <span className="cursor-default inline-flex items-center gap-1">
                                  Auto-approve at 5/5
                                  <Info
                                    size={14}
                                    className="text-ds-text-subtle/70"
                                  />
                                </span>
                              }
                            />
                            <TooltipContent
                              side="top"
                              className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                            >
                              Submit a GitHub approval (not merge) when the
                              review scores 5/5 (Safe to Merge). Overrides the
                              org-level default for this repository.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-ds-text-subtle tracking-wider"
                        style={{
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          lineHeight: '1.5',
                          width: '112px',
                        }}
                      >
                        <TooltipProvider delay={200}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <span className="cursor-default inline-flex items-center gap-1">
                                  Agent Insights
                                  <Info
                                    size={14}
                                    className="text-ds-text-subtle/70"
                                  />
                                </span>
                              }
                            />
                            <TooltipContent
                              side="top"
                              className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                            >
                              Collect agent-session insights (spend, tasks,
                              behaviour) from coding agents run against this
                              repository. Turning it off stops future
                              collection; insights already collected are kept.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`bg-ds-surface-white transition-opacity duration-200 ${
                      isFetchingRepositories && !isLoadingRepositories
                        ? 'opacity-60'
                        : ''
                    }`}
                  >
                    <tr>
                      <td colSpan={8} className="p-0">
                        <div>
                          {/* key remounts the rows after a settings write so the
                              `rows-resort` sweep replays over the new order */}
                          <div key={resortToken} className="rows-resort">
                            {filteredRepositories.map((repo, index) => {
                              return (
                                <div
                                  key={
                                    repo.repoUUID ||
                                    repo.name ||
                                    `repo-${index}`
                                  }
                                  className="border-b border-ds-border-subtlest"
                                >
                                  <table
                                    className="w-full min-w-[948px]"
                                    style={{ tableLayout: 'fixed' }}
                                  >
                                    <colgroup>
                                      <col style={{ width: '44px' }} />
                                      <col style={{ width: '280px' }} />
                                      <col style={{ width: '128px' }} />
                                      <col style={{ width: '96px' }} />
                                      <col style={{ width: '96px' }} />
                                      <col style={{ width: '96px' }} />
                                      <col style={{ width: '96px' }} />
                                      <col style={{ width: '112px' }} />
                                    </colgroup>
                                    <tbody>
                                      <tr className="hover:bg-ds-gray-subtlest/50">
                                        <td
                                          className="px-3 py-4 text-center align-middle"
                                          style={{ width: '44px' }}
                                        >
                                          <Checkbox
                                            checked={selectedRepos.has(
                                              repo.name
                                            )}
                                            onCheckedChange={(checked) => {
                                              setSelectedRepos((prev) => {
                                                const next = new Set(prev);
                                                if (checked === true)
                                                  next.add(repo.name);
                                                else next.delete(repo.name);
                                                return next;
                                              });
                                            }}
                                            aria-label={`Select ${repo.name}`}
                                            className="border-ds-border-subtlest data-checked:bg-ds-brand-subtle data-checked:border-ds-brand-subtle data-checked:text-ds-surface-white"
                                          />
                                        </td>
                                        <td
                                          className="pl-0 pr-4 py-4"
                                          style={{ width: '280px' }}
                                        >
                                          <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                              <span className="font-medium text-ds-text-darker truncate min-w-0 flex-1">
                                                {repo.name}
                                              </span>
                                              {repo.isBidirectional && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ds-warning-subtlest text-ds-warning-default">
                                                  bi-dep
                                                </span>
                                              )}
                                              {repo.status === 'Failed' && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ds-danger-subtlest text-ds-danger-darkest/30">
                                                  Error
                                                </span>
                                              )}
                                            </div>
                                            {repo.description && (
                                              <span className="text-xs text-ds-text-subtle line-clamp-1">
                                                {repo.description.length > 80
                                                  ? `${repo.description.substring(0, 80)}...`
                                                  : repo.description}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td
                                          className="px-4 py-4 text-center"
                                          style={{ width: '128px' }}
                                        >
                                          {repo.dependencies.length > 0 ? (
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger
                                                  render={
                                                    <span className="text-sm text-ds-text-darker cursor-pointer">
                                                      {repo.dependencies.length}{' '}
                                                      {repo.dependencies
                                                        .length === 1
                                                        ? 'linked repo'
                                                        : 'linked repos'}
                                                    </span>
                                                  }
                                                />
                                                <TooltipContent
                                                  side="top"
                                                  className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                                                >
                                                  <div className="space-y-1">
                                                    {repo.dependencies.map(
                                                      (depName) => (
                                                        <div
                                                          key={depName}
                                                          className="text-sm text-ds-text-darker"
                                                        >
                                                          {depName}
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          ) : (
                                            <span className="text-sm text-ds-text-subtle">
                                              -
                                            </span>
                                          )}
                                        </td>
                                        {!repo.hasPRBot ? (
                                          // No PR bot installation: the per-repo
                                          // settings cannot be written yet, so
                                          // offer the install instead of five
                                          // toggles that all fail.
                                          <td
                                            colSpan={5}
                                            className="px-4 py-4 text-right align-middle"
                                          >
                                            {/* sticky keeps the button parked at
                                                the visible right edge: the table
                                                is wider than most viewports, and
                                                the action must not scroll out of
                                                reach sideways. */}
                                            <div className="sticky right-4 inline-flex">
                                              <ShadcnButton
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                  handleAddRepoRow(repo)
                                                }
                                                disabled={isSyncInProgress}
                                              >
                                                Grant access
                                              </ShadcnButton>
                                            </div>
                                          </td>
                                        ) : (
                                          <>
                                            <td
                                              className="px-4 py-4 text-center"
                                              style={{ width: '96px' }}
                                            >
                                              <div className="flex items-center justify-center">
                                                <Switch
                                                  checked={
                                                    repo.enableAutoComments
                                                  }
                                                  onCheckedChange={() =>
                                                    repo.enableAutoComments
                                                      ? setMultiSelectModal({
                                                          field:
                                                            'enable_auto_comments',
                                                          repo,
                                                        })
                                                      : handleToggleSetting(
                                                          repo,
                                                          'enable_auto_comments',
                                                          repo.enableAutoComments
                                                        )
                                                  }
                                                  disabled={togglingRepos.has(
                                                    `${repo.name}:enable_auto_comments`
                                                  )}
                                                  className="data-checked:bg-ds-brand-subtle"
                                                />
                                              </div>
                                            </td>
                                            <td
                                              className="px-4 py-4 text-center"
                                              style={{ width: '96px' }}
                                            >
                                              <div className="flex items-center justify-center">
                                                <Switch
                                                  checked={repo.prReviewBot}
                                                  onCheckedChange={() => {
                                                    if (repo.prReviewBot) {
                                                      setMultiSelectModal({
                                                        field: 'pr_evaluation',
                                                        repo,
                                                      });
                                                    } else {
                                                      handleTogglePRBot(
                                                        repo.name
                                                      );
                                                    }
                                                  }}
                                                  disabled={togglingRepos.has(
                                                    repo.name
                                                  )}
                                                  className="data-checked:bg-ds-brand-subtle"
                                                />
                                              </div>
                                            </td>
                                            <td
                                              className="px-4 py-4 text-center"
                                              style={{ width: '96px' }}
                                            >
                                              <div className="flex items-center justify-center">
                                                <Switch
                                                  checked={repo.enableDedup}
                                                  onCheckedChange={() =>
                                                    handleToggleDedup(repo)
                                                  }
                                                  disabled={togglingRepos.has(
                                                    `${repo.name}:dedup`
                                                  )}
                                                  className="data-checked:bg-ds-brand-subtle"
                                                />
                                              </div>
                                            </td>
                                            <td
                                              className="px-4 py-4 text-center"
                                              style={{ width: '96px' }}
                                            >
                                              <div className="flex items-center justify-center">
                                                <Switch
                                                  checked={
                                                    repo.autoApproveOnSafe
                                                  }
                                                  onCheckedChange={() =>
                                                    handleToggleAutoApprove(
                                                      repo
                                                    )
                                                  }
                                                  disabled={togglingRepos.has(
                                                    `${repo.name}:autoapprove`
                                                  )}
                                                  className="data-checked:bg-ds-brand-subtle"
                                                />
                                              </div>
                                            </td>
                                            <td
                                              className="px-4 py-4 text-center"
                                              style={{ width: '112px' }}
                                            >
                                              <div className="flex items-center justify-center">
                                                <Switch
                                                  checked={
                                                    repo.agentInsightsEnabled
                                                  }
                                                  onCheckedChange={() =>
                                                    handleToggleAgentInsights(
                                                      repo
                                                    )
                                                  }
                                                  disabled={togglingRepos.has(
                                                    `${repo.name}:agentinsights`
                                                  )}
                                                  aria-label={`Collect agent insights for ${repo.name}`}
                                                  className="data-checked:bg-ds-brand-subtle"
                                                />
                                              </div>
                                            </td>
                                          </>
                                        )}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-ds-border-subtlest p-4">
                <span className="text-sm text-ds-text-subtle">
                  {reposTotalCount === 0
                    ? 'No repositories'
                    : (() => {
                        const visibleCount = filteredRepositories.length;
                        const start =
                          pagination.pageIndex * pagination.pageSize + 1;
                        const computedEnd =
                          pagination.pageIndex * pagination.pageSize +
                          visibleCount;
                        const end =
                          reposTotalCount > 0
                            ? Math.min(computedEnd, reposTotalCount)
                            : computedEnd;
                        return `Showing ${start}–${end} of ${reposTotalCount} repositories`;
                      })()}
                </span>
                <div className="flex items-center gap-2">
                  <ShadcnButton
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        pageIndex: Math.max(0, prev.pageIndex - 1),
                      }))
                    }
                    disabled={
                      pagination.pageIndex === 0 || isFetchingRepositories
                    }
                  >
                    Previous
                  </ShadcnButton>
                  <span className="text-sm text-ds-text-subtle">
                    Page {pagination.pageIndex + 1}
                  </span>
                  <ShadcnButton
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        pageIndex: prev.pageIndex + 1,
                      }))
                    }
                    disabled={!reposHasMore || isFetchingRepositories}
                  >
                    Next
                  </ShadcnButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom bar – Figma ref: padding 8, gap 4, X outline 1.5px #404040 rounded 8, separator 2px stretch #404040, groups padding 8 outline 1.5px rounded 8, gap 8 */}
        {selectedRepos.size > 0 && (
          <div
            className={`sticky bottom-0 left-0 right-0 z-10 flex justify-start ${isSyncInProgress ? 'pointer-events-none opacity-50' : ''}`}
            aria-disabled={isSyncInProgress}
          >
            <div
              className="motion-rise w-fit inline-flex items-center gap-1 rounded-xl bg-primitive-gray-800 border border-ds-border-default p-2 shadow-lg"
              role="region"
              aria-label="Bulk actions for selected repositories"
            >
              <button
                type="button"
                onClick={clearSelection}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-[1.5px] border-ds-border-subtle text-ds-text-subtlest hover:bg-ds-surface-off-white/10 transition-colors"
                aria-label="Clear selection"
              >
                <X className="h-5 w-5 shrink-0" />
              </button>
              <div className="flex items-center justify-center gap-2.5 px-2">
                <span className="text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                  {/* Must agree with the banner above. While "all matching" is
                      on the action's scope is the whole filtered set, so
                      showing the page's 20 here would understate what a toggle
                      is about to change. */}
                  {applyToAllMatching ? reposTotalCount : selectedRepos.size}{' '}
                  Repo
                  {(applyToAllMatching
                    ? reposTotalCount
                    : selectedRepos.size) === 1
                    ? ''
                    : 's'}{' '}
                  Selected
                </span>
              </div>
              <div
                className="flex shrink-0 flex-col self-stretch p-1.5"
                aria-hidden
              >
                <div className="w-0.5 min-h-0 flex-1 rounded-full bg-ds-border-default" />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center px-2">
                  <span className="text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                    Configure:
                  </span>
                </div>
                {(() => {
                  const prReviewSelected = Array.from(selectedRepos).map(
                    (name) => repositories.find((x) => x.name === name)
                  );
                  const prReviewsAllEnabled =
                    prReviewSelected.length > 0 &&
                    prReviewSelected.every(
                      (r) => r?.enableAutoComments ?? false
                    );
                  const prReviewsAllDisabled =
                    prReviewSelected.length > 0 &&
                    prReviewSelected.every(
                      (r) => !(r?.enableAutoComments ?? false)
                    );
                  const prReviewsMixed =
                    // Always the two-way menu while all-matching: the switch derives its
                    // checked state from the loaded page, so on a mixed page it
                    // renders unchecked and its only click submits `true` --
                    // leaving filter-wide DISABLE unreachable. A menu states
                    // both directions explicitly, which is what an unknown
                    // current state actually calls for.
                    applyToAllMatching ||
                    (prReviewSelected.length > 0 &&
                      !prReviewsAllEnabled &&
                      !prReviewsAllDisabled);
                  const isBulkEnabling = bulkEnableProgress !== null;
                  return (
                    <div
                      className={`flex items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-ds-border-default p-2 ${isBulkEnabling ? 'pointer-events-none opacity-70' : ''}`}
                      aria-busy={isBulkEnabling}
                    >
                      <span className="px-2 text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                        PR Reviews
                      </span>
                      {isBulkEnabling ? (
                        <div
                          className="flex items-center gap-2 px-1"
                          role="status"
                          aria-live="polite"
                        >
                          <Loader2 className="h-4 w-4 animate-spin text-ds-text-subtlest" />
                          <span className="text-xs font-medium text-ds-text-subtlest tabular-nums">
                            Enabling {bulkEnableProgress!.current}/
                            {bulkEnableProgress!.total}
                          </span>
                        </div>
                      ) : prReviewsMixed ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <ShadcnButton
                                type="button"
                                variant="ghost"
                                className="inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-ds-brand-subtler bg-ds-brand-subtlest shadow-sm transition-colors p-0 hover:opacity-90"
                                aria-label="Configure PR Reviews (mixed state)"
                              >
                                <span
                                  className="h-1 w-7 shrink-0 rounded-full bg-ds-brand-subtler"
                                  aria-hidden
                                />
                              </ShadcnButton>
                            }
                          />
                          <DropdownMenuContent
                            side="top"
                            align="center"
                            sideOffset={28}
                            className="min-w-[180px]"
                          >
                            <DropdownMenuItem
                              onClick={() => {
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_auto_comments: true },
                                    'Turned PR reviews on'
                                  );
                                  return;
                                }
                                handleMultiSelectEnablePRReview(
                                  Array.from(selectedRepos)
                                );
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-success-default/20 text-ds-success-default">
                                <Check className="h-3 w-3" />
                              </span>
                              Enable PR reviews
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_auto_comments: false },
                                    'Turned PR reviews off'
                                  );
                                  return;
                                }
                                const selectedReposList =
                                  Array.from(selectedRepos);
                                const first = repositories.find(
                                  (r) =>
                                    selectedReposList.includes(r.name) &&
                                    r.enableAutoComments
                                );
                                if (first) {
                                  setMultiSelectModal({
                                    field: 'enable_auto_comments',
                                    repo: first,
                                    restrictToSelected: true,
                                  });
                                }
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-danger-default/20 text-ds-danger-default">
                                <X className="h-3 w-3" />
                              </span>
                              Disable PR reviews
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Switch
                          checked={prReviewsAllEnabled}
                          onCheckedChange={(checked) => {
                            // "All matching" means the scope is the filter, not
                            // the loaded rows, so it goes to the server as one
                            // request rather than one per repo.
                            if (applyToAllMatching) {
                              // enable_auto_comments, matching what the
                              // per-repo path writes and what this switch is
                              // derived from. enable_pr_evaluation is the
                              // SEPARATE "PR Evaluation" column next to it.
                              void applyToEveryMatchingRepo(
                                { enable_auto_comments: checked },
                                checked
                                  ? 'Turned PR reviews on'
                                  : 'Turned PR reviews off'
                              );
                              return;
                            }
                            const names = Array.from(selectedRepos);
                            if (checked) {
                              handleMultiSelectEnablePRReview(names);
                            } else {
                              const first = repositories.find(
                                (r) =>
                                  names.includes(r.name) && r.enableAutoComments
                              );
                              if (first)
                                setMultiSelectModal({
                                  field: 'enable_auto_comments',
                                  repo: first,
                                  restrictToSelected: true,
                                });
                            }
                          }}
                          className="data-checked:bg-ds-brand-subtle data-unchecked:bg-primitive-gray-700 border border-ds-border-subtlest [&[data-checked]>*]:bg-ds-brand-subtler [&[data-unchecked]>*]:bg-ds-surface-off-white"
                        />
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const prEvalSelected = Array.from(selectedRepos).map((name) =>
                    repositories.find((x) => x.name === name)
                  );
                  const prEvalAllEnabled =
                    prEvalSelected.length > 0 &&
                    prEvalSelected.every((r) => r?.prReviewBot ?? false);
                  const prEvalAllDisabled =
                    prEvalSelected.length > 0 &&
                    prEvalSelected.every((r) => !(r?.prReviewBot ?? false));
                  const prEvalMixed =
                    // Always the two-way menu while all-matching: the switch derives its
                    // checked state from the loaded page, so on a mixed page it
                    // renders unchecked and its only click submits `true` --
                    // leaving filter-wide DISABLE unreachable. A menu states
                    // both directions explicitly, which is what an unknown
                    // current state actually calls for.
                    applyToAllMatching ||
                    (prEvalSelected.length > 0 &&
                      !prEvalAllEnabled &&
                      !prEvalAllDisabled);
                  return (
                    <div className="flex items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-ds-border-default p-2">
                      <span className="px-2 text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                        PR Evaluation
                      </span>
                      {prEvalMixed ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <ShadcnButton
                                type="button"
                                variant="ghost"
                                className="inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-ds-brand-subtler bg-ds-brand-subtlest shadow-sm transition-colors p-0 hover:opacity-90"
                                aria-label="Configure PR Evaluation (mixed state)"
                              >
                                <span
                                  className="h-1 w-7 shrink-0 rounded-full bg-ds-brand-subtler"
                                  aria-hidden
                                />
                              </ShadcnButton>
                            }
                          />
                          <DropdownMenuContent
                            side="top"
                            align="center"
                            sideOffset={28}
                            className="min-w-[180px]"
                          >
                            <DropdownMenuItem
                              onClick={async () => {
                                // Before the guard below: the filter-wide call
                                // needs only OrgUUID, and that guard also
                                // requires UserUUID, which is empty while the
                                // user store is still initialising -- so the
                                // action would silently do nothing.
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_pr_evaluation: true },
                                    'Turned PR evaluation on'
                                  );
                                  return;
                                }
                                if (!organization?.OrgUUID || !user?.UserUUID)
                                  return;
                                const names = Array.from(selectedRepos);
                                const toEnable = names.filter(
                                  (name) =>
                                    !repositories.find((r) => r.name === name)
                                      ?.prReviewBot
                                );
                                const isBitbucket =
                                  session?.user?.provider === 'bitbucket';
                                if (isBitbucket) {
                                  const targets = toEnable
                                    .map((name) =>
                                      repositories.find((r) => r.name === name)
                                    )
                                    .filter((r): r is Repository => Boolean(r));
                                  if (targets.length === 0) return;
                                  try {
                                    const response =
                                      await configurePRBotForOrgBitbucketBulk({
                                        UserUUID: user.UserUUID,
                                        OrgUUID: organization.OrgUUID,
                                        Items: targets.map((r) => ({
                                          RepoUrl: r.url,
                                          Enable: true,
                                        })),
                                        AccessToken:
                                          (user?.accessToken ||
                                            session?.user?.accessToken) ??
                                          '',
                                      });
                                    const failed = (
                                      response?.Results ?? []
                                    ).filter((row) => row.Error);
                                    if (response?.error || failed.length > 0) {
                                      console.error(
                                        'Failed to enable PR evaluation (bulk):',
                                        response?.error ?? failed
                                      );
                                      toast.show({
                                        title: 'Error',
                                        description: `Couldn't enable PR evaluation for ${failed.length || targets.length} repo(s). Please try again.`,
                                        status: 'error',
                                        duration: 4000,
                                        isClosable: true,
                                      });
                                      return;
                                    }
                                  } catch (err) {
                                    console.error(
                                      'Failed to enable PR evaluation (bulk):',
                                      err
                                    );
                                    toast.show({
                                      title: 'Error',
                                      description:
                                        "Couldn't enable PR evaluation. Please try again.",
                                      status: 'error',
                                      duration: 4000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                } else {
                                  for (const repoName of toEnable) {
                                    const result = await togglePRBotEnabled({
                                      repoName,
                                      orgUUID: organization.OrgUUID,
                                      enable_pr_evaluation: true,
                                    });
                                    if (!result.success) {
                                      console.error(
                                        `Failed to enable PR evaluation for ${repoName}:`,
                                        result.error
                                      );
                                      toast.show({
                                        title: 'Error',
                                        description: `Couldn't enable PR evaluation for ${repoName}. Please try again.`,
                                        status: 'error',
                                        duration: 4000,
                                        isClosable: true,
                                      });
                                      return;
                                    }
                                  }
                                }
                                refetchAndSweepRows();
                                if (toEnable.length > 0) {
                                  toast.show({
                                    title: 'Success',
                                    description: `PR evaluation enabled for ${toEnable.length} repo${toEnable.length === 1 ? '' : 's'}`,
                                    status: 'success',
                                    duration: 4000,
                                    isClosable: true,
                                  });
                                }
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-success-default/20 text-ds-success-default">
                                <Check className="h-3 w-3" />
                              </span>
                              Enable PR evaluation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_pr_evaluation: false },
                                    'Turned PR evaluation off'
                                  );
                                  return;
                                }
                                const selectedReposList =
                                  Array.from(selectedRepos);
                                const first = repositories.find(
                                  (r) =>
                                    selectedReposList.includes(r.name) &&
                                    r.prReviewBot
                                );
                                if (first) {
                                  setMultiSelectModal({
                                    field: 'pr_evaluation',
                                    repo: first,
                                    restrictToSelected: true,
                                  });
                                }
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-danger-default/20 text-ds-danger-default">
                                <X className="h-3 w-3" />
                              </span>
                              Disable PR evaluation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Switch
                          checked={prEvalAllEnabled}
                          onCheckedChange={async (checked) => {
                            // Filter scope: one request for the whole matching
                            // set. Only repos that already have the PR bot
                            // installed are affected -- installing it is a
                            // GitHub-side flow that cannot be done in bulk.
                            if (applyToAllMatching) {
                              void applyToEveryMatchingRepo(
                                { enable_pr_evaluation: checked },
                                checked
                                  ? 'Turned PR evaluation on'
                                  : 'Turned PR evaluation off'
                              );
                              return;
                            }
                            const names = Array.from(selectedRepos);
                            if (checked) {
                              if (!organization?.OrgUUID || !user?.UserUUID)
                                return;
                              const toEnable = names.filter(
                                (name) =>
                                  !repositories.find((r) => r.name === name)
                                    ?.prReviewBot
                              );
                              const isBitbucket =
                                session?.user?.provider === 'bitbucket';
                              if (isBitbucket) {
                                const targets = toEnable
                                  .map((name) =>
                                    repositories.find((r) => r.name === name)
                                  )
                                  .filter((r): r is Repository => Boolean(r));
                                if (targets.length === 0) return;
                                try {
                                  const response =
                                    await configurePRBotForOrgBitbucketBulk({
                                      UserUUID: user.UserUUID,
                                      OrgUUID: organization.OrgUUID,
                                      Items: targets.map((r) => ({
                                        RepoUrl: r.url,
                                        Enable: true,
                                      })),
                                    });
                                  const failed = (
                                    response?.Results ?? []
                                  ).filter((row) => row.Error);
                                  if (response?.error || failed.length > 0) {
                                    console.error(
                                      'Failed to enable PR evaluation (bulk):',
                                      response?.error ?? failed
                                    );
                                    toast.show({
                                      title: 'Error',
                                      description: `Couldn't enable PR evaluation for ${failed.length || targets.length} repo(s). Please try again.`,
                                      status: 'error',
                                      duration: 4000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                } catch (err) {
                                  console.error(
                                    'Failed to enable PR evaluation (bulk):',
                                    err
                                  );
                                  toast.show({
                                    title: 'Error',
                                    description:
                                      "Couldn't enable PR evaluation. Please try again.",
                                    status: 'error',
                                    duration: 4000,
                                    isClosable: true,
                                  });
                                  return;
                                }
                              } else {
                                for (const repoName of toEnable) {
                                  const result = await togglePRBotEnabled({
                                    repoName,
                                    orgUUID: organization.OrgUUID,
                                    enable_pr_evaluation: true,
                                  });
                                  if (!result.success) {
                                    console.error(
                                      `Failed to enable PR evaluation for ${repoName}:`,
                                      result.error
                                    );
                                    toast.show({
                                      title: 'Error',
                                      description: `Couldn't enable PR evaluation for ${repoName}. Please try again.`,
                                      status: 'error',
                                      duration: 4000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                }
                              }
                              refetchAndSweepRows();
                              if (toEnable.length > 0) {
                                toast.show({
                                  title: 'Success',
                                  description: `PR evaluation enabled for ${toEnable.length} repo${toEnable.length === 1 ? '' : 's'}`,
                                  status: 'success',
                                  duration: 4000,
                                  isClosable: true,
                                });
                              }
                            } else {
                              const first = repositories.find(
                                (r) => names.includes(r.name) && r.prReviewBot
                              );
                              if (first)
                                setMultiSelectModal({
                                  field: 'pr_evaluation',
                                  repo: first,
                                  restrictToSelected: true,
                                });
                            }
                          }}
                          className="data-checked:bg-ds-brand-subtle data-unchecked:bg-primitive-gray-700 border border-ds-border-subtlest [&[data-checked]>*]:bg-ds-brand-subtler [&[data-unchecked]>*]:bg-ds-surface-off-white"
                        />
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const dedupSelected = Array.from(selectedRepos).map((name) =>
                    repositories.find((x) => x.name === name)
                  );
                  const dedupAllEnabled =
                    dedupSelected.length > 0 &&
                    dedupSelected.every((r) => r?.enableDedup ?? false);
                  const dedupAllDisabled =
                    dedupSelected.length > 0 &&
                    dedupSelected.every((r) => !(r?.enableDedup ?? false));
                  const dedupMixed =
                    // Always the two-way menu while all-matching: the switch derives its
                    // checked state from the loaded page, so on a mixed page it
                    // renders unchecked and its only click submits `true` --
                    // leaving filter-wide DISABLE unreachable. A menu states
                    // both directions explicitly, which is what an unknown
                    // current state actually calls for.
                    applyToAllMatching ||
                    (dedupSelected.length > 0 &&
                      !dedupAllEnabled &&
                      !dedupAllDisabled);
                  return (
                    <div className="flex items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-ds-border-default p-2">
                      <span className="px-2 text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                        Deduplication
                      </span>
                      {dedupMixed ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <ShadcnButton
                                type="button"
                                variant="ghost"
                                className="inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-ds-brand-subtler bg-ds-brand-subtlest shadow-sm transition-colors p-0 hover:opacity-90"
                                aria-label="Configure Deduplication (mixed state)"
                              >
                                <span
                                  className="h-1 w-7 shrink-0 rounded-full bg-ds-brand-subtler"
                                  aria-hidden
                                />
                              </ShadcnButton>
                            }
                          />
                          <DropdownMenuContent
                            side="top"
                            align="center"
                            sideOffset={28}
                            className="min-w-[180px]"
                          >
                            <DropdownMenuItem
                              onClick={() => {
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_dedup: true },
                                    'Turned deduplication on'
                                  );
                                  return;
                                }
                                Array.from(selectedRepos).forEach(
                                  (repoName) => {
                                    const repo = repositories.find(
                                      (r) => r.name === repoName
                                    );
                                    if (repo && !repo.enableDedup)
                                      handleToggleDedup(repo);
                                  }
                                );
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-success-default/20 text-ds-success-default">
                                <Check className="h-3 w-3" />
                              </span>
                              Enable deduplication
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                if (applyToAllMatching) {
                                  void applyToEveryMatchingRepo(
                                    { enable_dedup: false },
                                    'Turned deduplication off'
                                  );
                                  return;
                                }
                                Array.from(selectedRepos).forEach(
                                  (repoName) => {
                                    const repo = repositories.find(
                                      (r) => r.name === repoName
                                    );
                                    if (repo && repo.enableDedup)
                                      handleToggleDedup(repo);
                                  }
                                );
                              }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-danger-default/20 text-ds-danger-default">
                                <X className="h-3 w-3" />
                              </span>
                              Disable deduplication
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Switch
                          checked={dedupAllEnabled}
                          onCheckedChange={(checked) => {
                            // Filter scope: one request instead of a toggle per
                            // repo (which at 243 rows would be 243 requests).
                            if (applyToAllMatching) {
                              void applyToEveryMatchingRepo(
                                { enable_dedup: checked },
                                checked
                                  ? 'Turned deduplication on'
                                  : 'Turned deduplication off'
                              );
                              return;
                            }
                            Array.from(selectedRepos).forEach((repoName) => {
                              const repo = repositories.find(
                                (r) => r.name === repoName
                              );
                              if (repo && repo.enableDedup !== checked)
                                handleToggleDedup(repo);
                            });
                          }}
                          className="data-checked:bg-ds-brand-subtle data-unchecked:bg-primitive-gray-700 border border-ds-border-subtlest [&[data-checked]>*]:bg-ds-brand-subtler [&[data-unchecked]>*]:bg-ds-surface-off-white"
                        />
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const aiSelected = Array.from(selectedRepos)
                    .map((name) => repositories.find((x) => x.name === name))
                    .filter((r): r is Repository => Boolean(r));
                  const aiAllEnabled =
                    aiSelected.length > 0 &&
                    aiSelected.every((r) => r.agentInsightsEnabled);
                  const aiAllDisabled =
                    aiSelected.length > 0 &&
                    aiSelected.every((r) => !r.agentInsightsEnabled);
                  const aiMixed =
                    aiSelected.length > 0 && !aiAllEnabled && !aiAllDisabled;
                  return (
                    <div className="flex items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-ds-border-default p-2">
                      <span className="px-2 text-sm font-medium leading-[20.3px] text-ds-text-subtlest">
                        Agent Insights
                      </span>
                      {aiMixed ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <ShadcnButton
                                type="button"
                                variant="ghost"
                                className="inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-ds-brand-subtler bg-ds-brand-subtlest shadow-sm transition-colors p-0 hover:opacity-90"
                                aria-label="Configure Agent Insights (mixed state)"
                              >
                                <span
                                  className="h-1 w-7 shrink-0 rounded-full bg-ds-brand-subtler"
                                  aria-hidden
                                />
                              </ShadcnButton>
                            }
                          />
                          <DropdownMenuContent
                            side="top"
                            align="center"
                            sideOffset={28}
                            className="min-w-[180px]"
                          >
                            <DropdownMenuItem
                              onClick={() =>
                                handleBulkAgentInsights(
                                  aiSelected
                                    .filter((r) => !r.agentInsightsEnabled)
                                    .map((r) => r.name),
                                  true
                                )
                              }
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-success-default/20 text-ds-success-default">
                                <Check className="h-3 w-3" />
                              </span>
                              Collect agent insights
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                handleBulkAgentInsights(
                                  aiSelected
                                    .filter((r) => r.agentInsightsEnabled)
                                    .map((r) => r.name),
                                  false
                                )
                              }
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-danger-default/20 text-ds-danger-default">
                                <X className="h-3 w-3" />
                              </span>
                              Stop collecting
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Switch
                          checked={aiAllEnabled}
                          onCheckedChange={(checked: boolean) =>
                            handleBulkAgentInsights(
                              aiSelected
                                .filter(
                                  (r) => r.agentInsightsEnabled !== checked
                                )
                                .map((r) => r.name),
                              checked
                            )
                          }
                          className="data-checked:bg-ds-brand-subtle data-unchecked:bg-primitive-gray-700 border border-ds-border-subtlest [&[data-checked]>*]:bg-ds-brand-subtler [&[data-unchecked]>*]:bg-ds-surface-off-white"
                        />
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Warning dialog when disabling a toggle (no confirmation when enabling). */}
      <Dialog
        open={Boolean(confirmToggle)}
        onOpenChange={(open) => !open && setConfirmToggle(null)}
      >
        <DialogContent className="bg-ds-surface-white border border-ds-border-subtlest rounded-lg md:rounded-xl p-4 mx-4 max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="text-base md:text-lg font-medium text-ds-text-darker">
              Disable setting
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center gap-4 py-4">
            <AlertTriangle
              className="h-10 w-10 text-ds-danger-default shrink-0"
              aria-hidden
            />
            {confirmToggle && (
              <span className="text-sm md:text-base font-normal text-ds-text-subtle text-center">
                Are you sure you want to disable{' '}
                <span className="font-medium text-ds-text-darker">
                  {TOGGLE_LABELS[confirmToggle.field]}
                </span>{' '}
                for{' '}
                <span className="font-medium text-ds-text-darker">
                  {confirmToggle.repo.name}
                </span>
                ?
              </span>
            )}
            <div className="flex gap-4 justify-center mt-2 w-full">
              <ShadcnButton
                variant="outline"
                onClick={() => setConfirmToggle(null)}
              >
                Cancel
              </ShadcnButton>
              <ShadcnButton variant="destructive" onClick={handleConfirmToggle}>
                Disable
              </ShadcnButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Provider Modal (GitLab / Bitbucket) */}
      <Dialog
        open={Boolean(providerModal)}
        onOpenChange={(open) => !open && setProviderModal(null)}
      >
        <DialogContent className="bg-ds-surface-white border border-ds-border-subtlest max-w-[500px] p-8">
          <DialogHeader>
            <DialogTitle>
              {providerModal?.provider === 'bitbucket' ? 'Bitbucket' : 'GitLab'}{' '}
              integration
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6 w-[450px] max-w-full p-4 pt-0">
            {providerModal?.provider === 'gitlab' && providerModal?.repoUrl ? (
              <p className="text-sm text-ds-text-subtle">
                Entelligence needs a Group Access Token to install the PR review
                bot on{' '}
                <span className="font-medium text-ds-text-darker">
                  {providerModal.repoUrl}
                </span>
                . This is required for private repositories.
              </p>
            ) : (
              <div className="space-y-2">
                <Label>
                  {providerModal?.provider === 'bitbucket'
                    ? 'Bitbucket'
                    : 'GitLab'}{' '}
                  Repository URL
                </Label>
                <Input
                  placeholder={
                    providerModal?.provider === 'bitbucket'
                      ? 'Enter https URL (e.g. https://bitbucket.org/workspace/repo)'
                      : 'Enter https URL'
                  }
                  value={providerModal?.repoUrl ?? ''}
                  onChange={(e) =>
                    setProviderModal((prev) =>
                      prev ? { ...prev, repoUrl: e.target.value } : prev
                    )
                  }
                  type="url"
                />
              </div>
            )}
            {providerModal?.provider === 'gitlab' && (
              <div className="space-y-2">
                <Label>Group Access Token</Label>
                <Input
                  placeholder="Enter Group Access Token"
                  value={providerModal?.groupAccessToken ?? ''}
                  onChange={(e) =>
                    setProviderModal((prev) =>
                      prev
                        ? { ...prev, groupAccessToken: e.target.value }
                        : prev
                    )
                  }
                />
              </div>
            )}
            <ShadcnButton
              className="w-full"
              onClick={installProviderWebhook}
              disabled={!providerModal?.repoUrl || providerModal?.isLoading}
            >
              {providerModal?.isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Submit'
              )}
            </ShadcnButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-select modal: disable PR Review or PR Evaluation for selected repos */}
      {multiSelectModal && (
        <DisableReposMultiSelectModal
          open={Boolean(multiSelectModal)}
          onOpenChange={(open) => !open && setMultiSelectModal(null)}
          title={
            multiSelectModal.field === 'enable_auto_comments'
              ? 'Are you sure you want to disable PR reviews for the selected repositories?'
              : 'Are you sure you want to disable PR evaluation for the selected repositories?'
          }
          searchPlaceholder="Search Repositories"
          repoNames={
            multiSelectModal.restrictToSelected
              ? Array.from(selectedRepos)
              : [multiSelectModal.repo.name]
          }
          // Bulk bar may select repos from pages not currently loaded —
          // page the DB for feature-enabled names instead of the table page.
          loadFeatureScope={
            multiSelectModal.restrictToSelected
              ? multiSelectModal.field
              : undefined
          }
          restrictToNames={
            multiSelectModal.restrictToSelected
              ? Array.from(selectedRepos)
              : undefined
          }
          reposWithFeatureEnabled={
            multiSelectModal.field === 'enable_auto_comments'
              ? reposWithPRReviewEnabled
              : reposWithPREvaluationEnabled
          }
          initialSelectedRepo={multiSelectModal.repo.name}
          initialSelectedRepos={
            multiSelectModal.restrictToSelected
              ? Array.from(selectedRepos)
              : [multiSelectModal.repo.name]
          }
          confirmLabel={
            multiSelectModal.field === 'enable_auto_comments'
              ? 'Yes, Disable PR Reviews'
              : 'Yes, Disable PR Evaluation'
          }
          onConfirm={handleMultiSelectDisable}
          showRepoIcon
        />
      )}

      <PermissionInfoModal
        isOpen={showPermissionInfo}
        onClose={() => {
          setShowPermissionInfo(false);
          setShowBackButton(false);
        }}
        showBackButton={showBackButton}
      />

      <InstallPrBotModal
        isOpen={installModal !== null}
        onClose={() => setInstallModal(null)}
        // First token only: GitOrgName holds a comma-separated list on a
        // workspace connected to several git orgs, and the install page
        // preselects the account behind the first one.
        // The org being installed into: the one just linked when the dialog
        // came from that flow, otherwise the workspace's first, which is what
        // suggested_target_id preselects for the other entry points.
        orgName={
          installModal?.target
            ? installModal.target.login
            : loggedOrg?.GitOrgName?.split(',')[0]?.trim() || undefined
        }
        repoName={installModal?.repoName}
        orgChoices={installOrgChoices}
        onProceed={(org) => goToGithubInstall(installModal?.target?.id, org)}
      />

      {/* Bitbucket App Password Modal */}
      <BitbucketAppPasswordModal
        isOpen={showBitbucketTokenModal}
        onClose={() => {
          setShowBitbucketTokenModal(false);
          pendingBitbucketRepoUrlRef.current = null;
        }}
        onSuccess={() => {
          onBitbucketTokenSaved();
          // If there was a pending repo config, resume it directly
          const pendingUrl = pendingBitbucketRepoUrlRef.current;
          pendingBitbucketRepoUrlRef.current = null;
          if (pendingUrl) proceedWithBitbucketConfig(pendingUrl);
        }}
        orgUUID={organization?.OrgUUID ?? ''}
      />
    </div>
  );
}
